// pages/sections/groups/DataQualityPanel.js
// Renders DQ issues organized by the canonical 4-phase Client Groups workflow:
//   1. Resolve invalid data (must clear before moving on)
//   2. Confirm signer groups (review + accept/reject suggestions, then signoff)
//   3. Confirm relationship groups (only after SG signoff)
//   4. Standalone 1040 review (the residual)
//
// Phase 1 is "fix what's broken." Until it's clear, partners should focus
// there — Phases 2-4 are visible but de-emphasized. When Phase 2 writes ship,
// we'll harden this into actual gating; for now de-emphasis is enough cue.
//
// Keep ISSUE_META + ISSUE_PHASE in sync with clientGroupsIngest.js.

import { cloneElement, useEffect, useMemo, useRef, useState } from 'react';
import { OptimisticContext, useOptimistic } from './optimisticContext';
import ResolveModal from './ResolveModal';
import MarkNlcButton from './MarkNlcButton';
import SplitMemberButton from './SplitMemberButton';
import RgCompareCards from './RgCompareCards';
import PhaseSignoffBar from './PhaseSignoffBar';
import BulkConfirmBar from './BulkConfirmBar';
import PromoteSgParentButton from './PromoteSgParentButton';
import SgReviewPanel from './SgReviewPanel';
import { useNameFor } from './useNameFor';
import { useAuth } from '../../../auth/AuthContext';
import { useTourDemo } from '../../../lib/tourDemo';
import { postResolveAction } from './resolveActionQueue';
import { kindOf } from './resolveVerbConfig';
import { emitToast, errorText } from './toast';
import ClientChip, { rememberClientNames } from './ClientChip';

// Maps phase number → audit_log phase key the signoff endpoint expects.
// NOTE: the SG ('sg') signoff is deliberately NOT here — it's rendered on
// the Signer Group Review step (Step 3), not Phase 2, since that review
// is the holistic "all my signer groups are correct" checkpoint that
// gates the RG phase. See SgReviewPanel.
const PHASE_SIGNOFF_KEY = { 3: 'rg', 4: 'standalone' };

export const ISSUE_META = {
  ORPHAN_CLIENT_RG: {
    label: 'Orphan client (no Relationship Group)',
    note:  'Active client has no Relationship Group yet. Needs a Relationship Group assignment.',
    resolveLabel: 'Assign Relationship Group',
  },
  KNUULA_MISSING_SG: {
    label: 'Knuula missing Signer Group',
    note:  'Client has no Signer Group on file and no live Knuula group (likely never received a letter).',
    resolveLabel: 'Assign Signer Group',
  },
  SG_SOURCE_DISAGREEMENT: {
    label: 'Signer Group source disagreement (Knuula vs PE / stored)',
    note:  'Knuula disagrees with PE’s Signer Group hint, or with the Signer Group on file for this client. Partner picks the truth.',
    resolveLabel: 'Pick truth',
  },
  SG_NEEDS_CONFIRMATION: {
    label: 'Signer Group needs partner confirmation',
    note:  'A Signer Group is suggested (Knuula preferred) but no partner has confirmed it yet.',
    resolveLabel: 'Confirm Signer Group',
  },
  SG_TRUST_ESTATE_MIXED: {
    label: 'Estate and Trust together in a group with an Individual',
    note:  'A group with an Individual may hold Estate clients or Trust clients, but not both kinds. Split the Estate(s) or the Trust(s) out into their own Signer Group.',
    resolveLabel: 'Split group',
  },
  SG_NON_SMLLC_MIXED: {
    label: 'Business that cannot be a SMLLC in a Signer Group with an Individual/Estate/Trust',
    note:  'Only an LLC, LLP, Sole Proprietor or Partnership can be a single-member LLC on a personal return. Any other business type (S-Corp, C Corp, Corporation, Non-Profit, …) cannot share a Signer Group with an Individual, Estate or Trust. Split it out — there is nothing to confirm.',
    resolveLabel: 'Split group',
  },
  SG_BUSINESS_INDIVIDUAL_MIXED: {
    label: 'Business or Estate/Trust mixed with an Individual in same Signer Group',
    note:  'An LLC, LLP, Sole Proprietor or Partnership with an Individual / Estate / Trust, or an Estate / Trust with an Individual. Legitimate only if the Business is a SMLLC on the personal return (or the Estate / Trust truly belongs with that Individual). Confirm or split.',
    resolveLabel: 'Confirm SMLLC / split',
  },
  SG_MULTI_INDIVIDUAL_PARENT: {
    label: 'Individual child under Individual parent',
    note:  'Legitimate only for dependent minors. Needs confirmation or split.',
    resolveLabel: 'Confirm minor / split',
  },
  NLC_PARENT_NEEDS_SUCCESSOR: {
    label: 'Group parent or anchor marked No Longer Client — choose a new one',
    note:  'The client that led this Signer Group (or anchored this Relationship Group) was marked No Longer Client, so the clients still in the group have no parent. Choose who leads it: promote one of them, disband the group, or move it into another. This is a structural fix — it applies even to groups that were already signed off.',
    resolveLabel: 'Choose new parent',
  },
  SG_PARENT_INACTIVE_IN_PE: {
    label: 'Signer Group parent no longer in PE',
    note:  'Knuula still lists this Signer Group parent, but the parent client is no longer in the active PE roster. The remaining active members need a new parent: expand the row and use “Make SG Parent” on the member who should lead the group.',
    resolveLabel: 'Pick new parent',
  },

  // Suggestion rows (severity INFO) — emitted by rebuildSuggestions() at
  // ingest time. Resolution surface = "Accept" (write the suggested grouping)
  // or "Dismiss" (mark resolved without writing).
  SG_SUGGESTED_BY_EMAIL: {
    label: 'Possible Signer Group (shared email)',
    note:  'Two or more clients share a personal email (taxpayer / spouse / signer). Consider unifying their Signer Group.',
    resolveLabel: 'Accept proposed group',
  },
  SG_SUGGESTED_BY_PHONE: {
    label: 'Possible Signer Group (shared phone)',
    note:  'Two or more clients share a contact phone number. Consider unifying their Signer Group.',
    resolveLabel: 'Accept proposed group',
  },
  SG_SUGGESTED_BY_ADDRESS: {
    label: 'Possible Signer Group (shared address)',
    note:  'Two or more clients share a street address. Consider unifying their Signer Group.',
    resolveLabel: 'Accept proposed group',
  },
  SG_SUGGESTED_BY_CLIENT_CODE: {
    label: 'Possible Signer Group (shared client code prefix)',
    note:  'Two or more clients share the first 5 of their client code. Common when related entities are assigned sub-accounts of a parent.',
    resolveLabel: 'Accept proposed group',
  },
  RG_SUGGESTED_BY_SG: {
    label: 'Possible Relationship Group (shared Signer Group)',
    note:  'Members of one Signer Group have mixed or missing Relationship Group assignments. Clients in the same Signer Group typically belong to the same Relationship Group.',
    resolveLabel: 'Confirm Relationship Group',
  },
  RG_SUGGESTED_BY_BG: {
    label: 'Possible Relationship Group (shared billing group)',
    note:  'Clients billed together typically belong to the same Relationship Group. Members of this billing group are split across Relationship Groups.',
    resolveLabel: 'Confirm Relationship Group',
  },
  RG_NEEDS_CONFIRMATION: {
    label: 'Relationship Group needs partner confirmation',
    note:  'This Relationship Group hasn’t been confirmed by a partner yet. Review the signer groups and clients grouped here, then confirm they belong together.',
    resolveLabel: 'Confirm Relationship Group',
  },
  STANDALONE_INDIVIDUAL_NEEDS_REVIEW: {
    label: 'Standalone individual (Step 5)',
    note:  'Active Individual client with no related entities — a one-member Signer Group inside a one-Signer-Group Relationship Group. Confirm they really are solo, or rescue them into an existing Relationship Group.',
    resolveLabel: 'Review standalone',
  },
};

// Maps each issue_code to its phase in the 4-phase workflow.
// Rule: phase by *what's being resolved*, not where the issue surfaced.
// SG-level data problems are Phase 1; SG attestation is Phase 2; everything
// RG-shaped (including missing RGs / BG-implies-RG conflicts) is Phase 3.
const ISSUE_PHASE = {
  // Phase 1 — invalid data that must be fixed before anything else.
  KNUULA_MISSING_SG:            1,
  SG_SOURCE_DISAGREEMENT:       1,
  SG_TRUST_ESTATE_MIXED:        1,
  SG_NON_SMLLC_MIXED:           1,
  SG_BUSINESS_INDIVIDUAL_MIXED: 1,
  SG_MULTI_INDIVIDUAL_PARENT:   1,
  SG_PARENT_INACTIVE_IN_PE:     1,
  NLC_PARENT_NEEDS_SUCCESSOR:   1,
  // Phase 2 — signer group attestation and matching suggestions.
  SG_NEEDS_CONFIRMATION:        2,
  SG_SUGGESTED_BY_EMAIL:        2,
  SG_SUGGESTED_BY_PHONE:        2,
  SG_SUGGESTED_BY_ADDRESS:      2,
  SG_SUGGESTED_BY_CLIENT_CODE:  2,
  // Phase 3 — relationship group attestation and matching suggestions.
  ORPHAN_CLIENT_RG:             3,
  RG_NEEDS_CONFIRMATION:        3,
  RG_SUGGESTED_BY_SG:           3,
  RG_SUGGESTED_BY_BG:           3,
  // Phase 4 — Standalone 1040 review (the residual after Phase 2/3 are clean).
  STANDALONE_INDIVIDUAL_NEEDS_REVIEW: 4,
};

const PHASES = [
  {
    n: 1,
    title: 'Resolve invalid data',
    subtitle: 'Fix mismatches, missing records, and rule violations before anything else.',
  },
  {
    n: 2,
    title: 'Confirm signer groups',
    subtitle: 'Confirm Knuula-suggested Signer Groups and review proposed groupings. You sign off in the next step.',
  },
  {
    n: 3,
    title: 'Confirm relationship groups',
    subtitle: 'Verify each Signer Group belongs to the correct Relationship Group.',
  },
  {
    n: 4,
    title: 'Review standalone individuals',
    subtitle: 'Confirm each solo 1040 is truly standalone or move into a Relationship Group.',
  },
];

export function phaseFor(issueCode) {
  return ISSUE_PHASE[issueCode] || 99;   // unknown codes fall to a "other" bucket
}

// Sort within a phase: ERROR → WARN → INFO, then by count descending.
const SEVERITY_RANK = { ERROR: 0, WARN: 1, INFO: 2 };

// Severity → user-facing label. The audit-log value stays 'WARN' / 'ERROR'
// / 'INFO' — only the badge text reads in plain English so partners aren't
// staring at developer constants.
const SEVERITY_LABEL = {
  ERROR: 'Error',
  WARN:  'Needs Confirmation',
  INFO:  'Info',
};

// Within a phase, certain issue codes need to be acted on BEFORE others
// because their resolution reshapes the cohort that downstream rules
// then re-evaluate. Anything in this map gets pinned to the top of its
// phase regardless of severity / count. Lower number = higher priority.
const ISSUE_PRIORITY_PINS = {
  // SG_SOURCE_DISAGREEMENT must come first in Step 1: the partner picks
  // Knuula vs PE as the truth, the cohort recomputes, and downstream
  // mixed-type rules re-evaluate against the corrected SG. Otherwise the
  // partner could spend time triaging a mixed-type warning that the
  // truth-pick would have changed.
  SG_SOURCE_DISAGREEMENT: 0,

  // Step 4 (Relationship Groups) explicit order:
  //   1. ORPHAN_CLIENT_RG       — ERROR: client has no RG at all, fix first.
  //   2. RG_SUGGESTED_BY_SG  ┐   "Possible Relationship Group" suggestions
  //   3. RG_SUGGESTED_BY_BG  ┘   come before the confirmation queue so the
  //   4. RG_NEEDS_CONFIRMATION    partner shapes groups before attesting them.
  // (Pins override severity/count within the step — see sortGroups.)
  ORPHAN_CLIENT_RG:       0,
  RG_SUGGESTED_BY_SG:     1,
  RG_SUGGESTED_BY_BG:     2,
  RG_NEEDS_CONFIRMATION:  3,
};

function sortGroups(groups) {
  return groups.sort((a, b) => {
    const pa = ISSUE_PRIORITY_PINS[a.issueCode];
    const pb = ISSUE_PRIORITY_PINS[b.issueCode];
    if (pa !== undefined || pb !== undefined) {
      // Both pinned → compare pin priorities. One pinned → pinned wins.
      if (pa !== undefined && pb !== undefined) return pa - pb;
      return pa !== undefined ? -1 : 1;
    }
    const ra = SEVERITY_RANK[a.severity] ?? 99;
    const rb = SEVERITY_RANK[b.severity] ?? 99;
    if (ra !== rb) return ra - rb;
    // Tiebreaker is alphabetical by issue_code (stable across resolves)
    // rather than count DESC — count tiebreakers reorder the section
    // list every time the partner resolves an issue, making the panel
    // feel like it's jumping even though the auto-advance logic isn't
    // firing. Severity already does the heavy lifting for prioritization.
    return a.issueCode.localeCompare(b.issueCode);
  });
}

// Editable group issues: the mixed-type rules render an expanded member list
// the partner edits in place (split a member out, mark NLC, etc.). There is
// exactly ONE open row per SG anchor for each of these codes, so the anchor
// alone is a stable, unique identity — and crucially it does NOT change when a
// member is moved out of the group. Keying the row on the anchor (rather than
// the full member set) means moving member A out doesn't remount the row, so a
// sibling member B's open modal survives A's refetch.
const EDITABLE_GROUP_CODES = new Set([
  'SG_BUSINESS_INDIVIDUAL_MIXED',
  'SG_TRUST_ESTATE_MIXED',
  'SG_NON_SMLLC_MIXED',
  'SG_MULTI_INDIVIDUAL_PARENT',
]);

// Stable identity for an issue ACROSS refetches. The backend regenerates
// row ids on every rebuildDqIssues (DELETE+INSERT → new IDENTITY values),
// so issue.id changes on each refetch even when the underlying issue is the
// same. Keying React rows + expansion state on id therefore remounts rows
// (closing any open inline modal) and collapses the expanded drilldown the
// moment any diResolveAction triggers a refetch.
//
// For editable group issues we key on (issue_code + anchor) so the identity is
// ALSO stable across membership changes (moving a member out). For every other
// code we include the sorted member set — those codes can repeat a primary
// across overlapping clusters (e.g. a client in two SG suggestions), so the
// member set is needed to keep rows distinct. This mirrors the DB's
// UX_dq_issues_open_signature unique index closely enough to stay unique among
// open rows while surviving the id regeneration.
export function issueSig(issue) {
  if (!issue) return null;
  // One task per NLC client AND layer (a signer-group task and a relationship-group task can
  // share the same primary client and members), so the layer + NLC client are part of the identity.
  if (issue.issueCode === 'NLC_PARENT_NEEDS_SUCCESSOR') {
    const d = issue.details || {};
    return `${issue.issueCode}|${d.layer || ''}|${d.nlcClientCode || ''}|${issue.primaryClientCode || ''}`;
  }
  if (EDITABLE_GROUP_CODES.has(issue.issueCode)) {
    return `${issue.issueCode}|${issue.primaryClientCode || ''}`;
  }
  const codes = Array.isArray(issue.affectedCodes)
    ? [...issue.affectedCodes].sort()
    : [];
  return `${issue.issueCode}|${issue.primaryClientCode || ''}|${codes.join(',')}`;
}

export default function DataQualityPanel({
  breakdown, issues, email, isAdmin, scope, period, signoffs, onResolved,
  testMode, testSessionId,
  // Spotlight-tour demo mode: render the panel fully un-gated with every issue
  // group expanded, so each tour step can frame the real functionality it
  // describes (Steps 3–5 would otherwise be locked and collapsed).
  demo = false,
  // CG_STEP5_EARLY_USERS (server): Step 5 opens for this partner before Steps 1-4 are done. Sign-off still waits.
  earlyStandalone = false,
  // Read-only RGs the caller partners a member in but doesn't anchor.
  rgMemberships = [],
  // Expansion state is owned by the parent so it survives panel remounts
  // (e.g. an error mid-refetch that briefly nulls data) and refetches
  // after a Resolve action. If the parent doesn't supply controllers we
  // fall back to local state.
  openCode: openCodeProp,
  setOpenCode: setOpenCodeProp,
  expandedIssueId: expandedIssueIdProp,
  setExpandedIssueId: setExpandedIssueIdProp,
}) {
  const [openCodeLocal, setOpenCodeLocal] = useState(null);
  const [expandedIssueIdLocal, setExpandedIssueIdLocal] = useState(null);
  const openCode             = openCodeProp        !== undefined ? openCodeProp        : openCodeLocal;
  const setOpenCode          = setOpenCodeProp     || setOpenCodeLocal;
  const expandedIssueId      = expandedIssueIdProp !== undefined ? expandedIssueIdProp : expandedIssueIdLocal;
  const setExpandedIssueId   = setExpandedIssueIdProp || setExpandedIssueIdLocal;
  const [resolveIssue, setResolveIssue]         = useState(null);   // null = modal closed
  // When a caller wants to skip the verb-list step and land the partner
  // directly on a specific verb's confirmation form (e.g., the
  // per-row "Ignore" button on SG suggestions), they pass the verb key
  // here. Cleared on modal close.
  const [resolveInitialVerb, setResolveInitialVerb] = useState(null);

  // Optimistic dismiss: STABLE SIGNATURES (issueSig) of issues the partner has
  // acted on locally that the server hasn't dropped yet. Keyed by signature —
  // NOT issue.id — on purpose: rebuildDqIssues regenerates every issue id on
  // each revalidate, so an id-keyed dismiss would be pruned the instant ANY
  // queued action's refetch lands, making not-yet-processed items flash back
  // into view one-by-one (the "processing item shows for a couple seconds"
  // bug). A signature survives the rebuild, so a queued item stays hidden
  // until its OWN action actually resolves it (its signature leaves the list).
  const [dismissedSigs, setDismissedSigs] = useState(() => new Set());

  // Pending client codes: codes with an in-flight diResolveAction. While
  // a code is pending, EVERY issue referencing it is hidden — regardless
  // of issue id. This is the loading-state companion to dismissedSigs:
  // when the partner rapidly accepts/ignores suggestions, the server
  // regenerates issue ids on each revalidate, so a freshly-refetched
  // overlapping cluster would otherwise pop back into view mid-flight.
  // Hiding by code keeps the row gone for the whole action lifecycle
  // (start → response → refetch settle → clear).
  const [pendingCodes, setPendingCodes] = useState(() => new Set());

  // When fresh data arrives (after refetch), prune dismissed signatures that no
  // longer exist in the server's view — the server caught up to our optimistic
  // state. Crucially, a signature that's STILL in the list (a queued action not
  // yet processed) is KEPT, so it stays hidden until its own action lands.
  // Identity-preserve the Set when nothing changes so downstream useMemo's
  // don't recompute needlessly.
  useEffect(() => {
    setDismissedSigs(prev => {
      if (prev.size === 0) return prev;
      const liveSigs = new Set(issues.map(i => issueSig(i)));
      const next = new Set();
      for (const sig of prev) if (liveSigs.has(sig)) next.add(sig);
      return next.size === prev.size ? prev : next;
    });
  }, [issues]);

  // Keep an open Resolve modal pointed at the FRESH issue object after a
  // refetch. Ids regenerate on rebuild, so the modal's captured `issue`
  // goes stale (its .id would 404 on dispatch). Re-point by stable
  // signature so the modal shows current data and acts on a valid row.
  // If the issue is genuinely gone (resolved), leave the modal as-is — the
  // partner's own submit closes it; we don't yank it out from under them.
  useEffect(() => {
    setResolveIssue(prev => {
      if (!prev) return prev;
      const sig = issueSig(prev);
      const fresh = issues.find(i => issueSig(i) === sig);
      return fresh || prev;
    });
  }, [issues]);

  // Keep an expanded row open across a refetch even when the action changed
  // the issue's signature. A member action with row-level loading often
  // mutates identity: "promote" repoints the anchor (primary_client_code),
  // "split" shrinks the member set (affected_codes) — either changes
  // issueSig, so a strict sig match would collapse the drilldown the partner
  // is mid-way through. We remember the expanded issue's rule + member set,
  // and when its exact signature disappears we re-point expansion to the
  // SAME rule's issue with the most overlapping members (its successor). If
  // nothing overlaps (the issue was genuinely resolved), it collapses.
  const lastExpandedRef = useRef(null);   // { sig, issueCode, codes:Set }
  useEffect(() => {
    if (!expandedIssueId) { lastExpandedRef.current = null; return; }
    const match = issues.find(i => issueSig(i) === expandedIssueId);
    if (match) {
      // Still present under its current signature — just refresh our memory.
      lastExpandedRef.current = {
        sig: expandedIssueId,
        issueCode: match.issueCode,
        codes: new Set(match.affectedCodes || []),
      };
      return;
    }
    // Only remap an expansion we were actively tracking (guards scope flips /
    // stale restored signatures from hijacking an unrelated row).
    const prev = lastExpandedRef.current;
    if (!prev || prev.sig !== expandedIssueId) return;
    let best = null;
    let bestOverlap = 0;
    for (const i of issues) {
      if (i.issueCode !== prev.issueCode) continue;
      const overlap = (i.affectedCodes || []).reduce(
        (n, c) => n + (prev.codes.has(c) ? 1 : 0), 0);
      if (overlap > bestOverlap) { bestOverlap = overlap; best = i; }
    }
    if (best && bestOverlap > 0) {
      const sig = issueSig(best);
      lastExpandedRef.current = {
        sig, issueCode: best.issueCode, codes: new Set(best.affectedCodes || []),
      };
      setExpandedIssueId(sig);
    } else {
      lastExpandedRef.current = null;
    }
  }, [issues, expandedIssueId, setExpandedIssueId]);

  // Only the explicitly-dismissed (acted-on) rows are removed. Rows whose
  // codes are merely PENDING (a sibling action touching the same code is
  // in flight) stay visible and render in a loading/disabled state — see
  // `pending` in IssueRow — so e.g. resolving client A's source
  // disagreement doesn't make client B's same-SG row vanish.
  const visibleIssues = useMemo(
    () => issues.filter(i => !dismissedSigs.has(issueSig(i))),
    [issues, dismissedSigs]
  );

  // Breakdown decrements as the partner dismisses, so phase counts +
  // gating chain stay in sync with what's actually showing.
  const visibleBreakdown = useMemo(() => {
    const map = new Map();
    for (const i of visibleIssues) {
      const key = i.issueCode + '|' + i.severity;
      map.set(key, (map.get(key) || 0) + 1);
    }
    const out = [];
    for (const [key, count] of map) {
      const [issueCode, severity] = key.split('|');
      out.push({ issueCode, severity, count });
    }
    return out;
  }, [visibleIssues]);

  // Optimistic-dismiss context value. Memoized on `issues` so the closure
  // for dismissByClientCode sees the current full list (NOT the filtered
  // view) — we want to dismiss based on server truth, not on what's
  // already been hidden.
  const optimisticValue = useMemo(() => {
    // dismissByIds / restoreIds operate on stable issue SIGNATURES (issueSig),
    // not row ids — see the dismissedSigs note above. Callers treat the tokens
    // as opaque (dismiss → get tokens → restore the same tokens on failure),
    // so the rename is invisible to them; ResolveModal passes issueSig(issue).
    function dismissByIds(sigs) {
      if (!sigs || sigs.length === 0) return;
      setDismissedSigs(prev => {
        const next = new Set(prev);
        for (const sig of sigs) next.add(sig);
        return next;
      });
    }
    function dismissByClientCode(code) {
      if (!code) return [];
      const matching = issues
        .filter(i => i.primaryClientCode === code || (i.affectedCodes || []).includes(code))
        .map(i => issueSig(i));
      dismissByIds(matching);
      return matching;
    }
    function restoreIds(sigs) {
      if (!sigs || sigs.length === 0) return;
      setDismissedSigs(prev => {
        const next = new Set(prev);
        for (const sig of sigs) next.delete(sig);
        return next;
      });
    }
    // Mark / clear client codes as pending an in-flight action. Components
    // call markPending(codes) at action start and clearPending(codes)
    // after the response + refetch settle (or on failure). While pending,
    // every issue referencing the code is hidden (see visibleIssues).
    function markPending(codes) {
      const list = Array.isArray(codes) ? codes.filter(Boolean) : [codes].filter(Boolean);
      if (list.length === 0) return;
      setPendingCodes(prev => {
        const next = new Set(prev);
        for (const c of list) next.add(c);
        return next;
      });
    }
    function clearPending(codes) {
      const list = Array.isArray(codes) ? codes.filter(Boolean) : [codes].filter(Boolean);
      if (list.length === 0) return;
      setPendingCodes(prev => {
        const next = new Set(prev);
        for (const c of list) next.delete(c);
        return next;
      });
    }
    return { dismissByIds, dismissByClientCode, restoreIds, markPending, clearPending, pendingCodes };
  }, [issues, pendingCodes]);

  // Group issues by code for fast render. Uses visibleIssues so dismissed
  // rows don't render in the expansion list.
  const byCode = useMemo(() => {
    const map = new Map();
    for (const i of visibleIssues) {
      if (!map.has(i.issueCode)) map.set(i.issueCode, []);
      map.get(i.issueCode).push(i);
    }
    // The NLC-successor tasks list per NLC client with the signer group task BEFORE the
    // relationship group task (the RG anchor can only be a signer group that exists).
    const nlcRows = map.get('NLC_PARENT_NEEDS_SUCCESSOR');
    if (nlcRows) {
      const layerRank = (i) => ((i.details && i.details.layer) === 'sg' ? 0 : 1);
      nlcRows.sort((a, b) =>
        String((a.details || {}).nlcClientCode || '').localeCompare(String((b.details || {}).nlcClientCode || '')) ||
        layerRank(a) - layerRank(b) ||
        String(a.primaryClientCode || '').localeCompare(String(b.primaryClientCode || '')));
    }
    return map;
  }, [visibleIssues]);

  // Bucket the breakdown by phase, then sort within phase. Uses
  // visibleBreakdown so per-phase counts reflect optimistic dismissals.
  const groupsByPhase = useMemo(() => {
    const buckets = new Map();
    for (const r of visibleBreakdown) {
      const p = phaseFor(r.issueCode);
      if (!buckets.has(p)) buckets.set(p, []);
      buckets.get(p).push({ ...r, meta: ISSUE_META[r.issueCode] || {} });
    }
    for (const arr of buckets.values()) sortGroups(arr);
    return buckets;
  }, [visibleBreakdown]);

  // When the partner finishes the issues in the currently-open code
  // group (count drops to 0 after a resolve), auto-advance openCode to
  // the next non-empty + non-gated group so they don't have to manually
  // click into the next section. Walks phases in order; skips gated
  // phases entirely so attention doesn't jump to a locked queue.
  // Defined right before groupsByPhase is consumed so the closure picks
  // up the latest derived state.
  useEffect(() => {
    if (!openCode) return;
    // Build a flat ordered list of all currently-visible groups across
    // all phases, mirroring the display order (phase 1 → 4, then
    // sortGroups within each).
    const flat = [];
    for (const p of PHASES) {
      const groups = groupsByPhase.get(p.n) || [];
      for (const g of groups) flat.push({ ...g, phaseN: p.n });
    }
    const isCandidate = (g) => g.count > 0 && !gateReason(g.phaseN);
    const currentIdx = flat.findIndex(g => g.issueCode === openCode);
    if (currentIdx < 0) {
      // openCode references a code that no longer appears (resolved
      // and the rule no longer fires, OR loaded stale from
      // sessionStorage). Pick the first available group rather than
      // closing the panel entirely — saves the partner an extra click.
      const next = flat.find(isCandidate);
      setOpenCode(next ? next.issueCode : null);
      return;
    }
    if (flat[currentIdx].count > 0) return;   // still has rows; stay put.
    for (let i = currentIdx + 1; i < flat.length; i++) {
      if (isCandidate(flat[i])) { setOpenCode(flat[i].issueCode); return; }
    }
    // Wrap to earlier groups in case the partner was at the end of the
    // list and the only remaining work is something they'd already
    // glossed past.
    for (let i = 0; i < currentIdx; i++) {
      if (isCandidate(flat[i])) { setOpenCode(flat[i].issueCode); return; }
    }
    // Nothing left to open.
    setOpenCode(null);
    // gateReason / setOpenCode are stable per render; depending on
    // openCode + groupsByPhase + signoffs covers every state change
    // that should trigger re-evaluation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCode, groupsByPhase, signoffs]);

  // Per-phase open counts and signoff state drive the hard-gate chain:
  //   Phase 2: gated until Phase 1 open count = 0
  //   Phase 3: gated until Phase 2 open count = 0 AND signoffs.sg
  //   Phase 4: gated until Phase 3 open count = 0 AND signoffs.rg
  // (Phase 1 has no signoff — partner just resolves all issues.)
  const phaseCounts = [0, 0, 0, 0, 0];  // index by phase number; [0] unused
  for (const [n, gs] of groupsByPhase.entries()) {
    phaseCounts[n] = gs.reduce((a, g) => a + g.count, 0);
  }
  const totalCount = (breakdown || []).reduce((a, g) => a + g.count, 0);

  // No sign-off freeze (Oct 2026 redesign): signing off a step stamps the partner's unreviewed
  // clients but never locks editing — every grouping stays viewable and changeable (only an
  // admin's Undo of a sign-off remains).

  // The "Relationship Groups you're part of" subsection counts toward Step 4
  // completion: every membership the partner is in must be confirmed before
  // they can sign off Step 4. Uses server state (each confirm triggers a
  // refetch that updates rgMemberships).
  const membershipsAllConfirmed = (rgMemberships || []).every(m => m.confirmed);

  // Partner-with-clean-data still needs to attest. If they have zero DQ
  // issues but at least one phase signoff is missing, surface the
  // attestation strip instead of the step cards.
  const allPhasesSignedOff = Boolean(
    signoffs && signoffs.sg && signoffs.rg && signoffs.standalone
  );

  function gateReason(phaseN) {
    if (demo) return null;   // tour: every phase open so each step shows its UI
    if (phaseN === 4 && earlyStandalone) return null;   // opened early for this partner; its sign-off is still gated (below)
    if (phaseN === 2) {
      if (phaseCounts[1] > 0) return 'Resolve every open Step 1 issue to unlock.';
      return null;
    }
    if (phaseN === 3) {
      if (phaseCounts[1] > 0) return 'Resolve every open Step 1 issue to unlock.';
      if (phaseCounts[2] > 0) return 'Resolve every open Step 2 issue to unlock.';
      if (!signoffs?.sg)      return 'Sign off Signer Groups (Step 3) to unlock.';
      return null;
    }
    if (phaseN === 4) {
      if (phaseCounts[1] > 0) return 'Resolve every open Step 1 issue to unlock.';
      if (phaseCounts[2] > 0) return 'Resolve every open Step 2 issue to unlock.';
      if (!signoffs?.sg)      return 'Sign off Signer Groups (Step 3) to unlock.';
      // Don't gate on phaseCounts[3]: Step 4's RG rows PERSIST (confirmed via a
      // reversible toggle, not "resolved"), so that count never reaches 0. The
      // Step 4 sign-off is the real completion signal — it already requires
      // every RG row + membership confirmed and any other RG issue cleared
      // (see canSignOff). So signing off Step 4 is exactly what unlocks Step 5.
      if (!signoffs?.rg)      return 'Sign off Relationship Groups (Step 4) to unlock.';
      return null;
    }
    return null;
  }

  // Once every step is attested we fall through to the full step view below, so the
  // partner can go back in and review their groups (each card shows "Signed off").
  if (totalCount === 0 && period && signoffs && !allPhasesSignedOff) {
    // Clean data, but at least one phase still needs the partner's
    // attestation. Render a focused mini-view with just the signoff bars.
    return (
      <div className="cg-empty cg-empty--signoffs-only">
        <h3>Data is clean — please attest each step</h3>
        <p>
          No open data-quality issues for {scope === 'mine' ? 'your clients' : 'any partner'} in
          the active period. Sign off each step below to complete the cycle.
        </p>
        <PhaseSignoffBar
          phase="sg"
          period={period}
          signoff={signoffs.sg}
          canSignOff={true}
          onChanged={onResolved}
          testMode={testMode}
          testSessionId={testSessionId}
          isAdmin={isAdmin}
        />
        <PhaseSignoffBar
          phase="rg"
          period={period}
          signoff={signoffs.rg}
          canSignOff={Boolean(signoffs.sg)}
          onChanged={onResolved}
          testMode={testMode}
          testSessionId={testSessionId}
          isAdmin={isAdmin}
        />
        <PhaseSignoffBar
          phase="standalone"
          period={period}
          signoff={signoffs.standalone}
          canSignOff={Boolean(signoffs.rg)}
          onChanged={onResolved}
          testMode={testMode}
          testSessionId={testSessionId}
          isAdmin={isAdmin}
        />
      </div>
    );
  }

  // Display step numbers for each rendered panel — independent of the
  // internal phase.n. The Signer Group Review panel sits between
  // Phase 2 and Phase 3 in the partner's view; everything after it
  // shifts by 1. PHASE-internal numbers (used for issue grouping,
  // gating, signoffs) stay 1-4 so the schema doesn't move.
  function displayStepNumberFor(phaseN) {
    return phaseN <= 2 ? phaseN : phaseN + 1;
  }

  return (
   <OptimisticContext.Provider value={optimisticValue}>
    <div className="cg-phases">
      {PHASES.map(p => {
        const groups = groupsByPhase.get(p.n) || [];
        const phaseTotal = groups.reduce((a, g) => a + g.count, 0);
        // Step 4 also surfaces the "Relationship Groups you're part of"
        // memberships as items in the card, so they count toward the card's
        // "X open" total (and keep it from reading "All clear" while
        // memberships remain). Kept separate from phaseTotal, which still
        // means "RG issues only" for the sign-off readiness math below.
        const membershipCount = p.n === 3 ? rgMemberships.length : 0;
        // …but only the unconfirmed ones are still OPEN (the header pill's count).
        const membershipOpenCount = p.n === 3 ? rgMemberships.filter(m => !m.confirmed).length : 0;
        // Hard phase gating — see gateReason() for the chain rules.
        const reason  = gateReason(p.n);
        const gated   = Boolean(reason);
        const isEmpty = (phaseTotal + membershipCount) === 0;

        const signoffKey = PHASE_SIGNOFF_KEY[p.n] || null;
        // RG-modifying buttons (MoveToRgButton on each MemberCard) are
        // only allowed once Phase 3 has unlocked. Per the strict phase
        // model, the partner finishes all SG work first; Phase 3 then
        // surfaces the residual RG mismatches via RG_SUGGESTED_BY_SG /
        // RG_NEEDS_CONFIRMATION and the partner reconciles there.
        const allowRgActions = p.n >= 3 && !gated;
        // Step 5 opened early: the review items are workable now, but it can only be attested after Step 4 is signed
        // (the server enforces it too, signoffBlockers).
        const signoffNote = (earlyStandalone && p.n === 4 && !signoffs?.rg)
          ? 'Step 5 is open for you to review early. It can be signed off once Step 4 (Relationship Groups) is signed off.'
          : null;
        const phaseSection = (
          <PhaseSection
            key={`phase-${p.n}`}
            signoffNote={signoffNote}
            phase={p}
            displayStep={displayStepNumberFor(p.n)}
            groups={groups}
            phaseTotal={phaseTotal}
            gated={gated}
            gateReason={reason}
            isEmpty={isEmpty}
            byCode={byCode}
            email={email}
            isAdmin={isAdmin}
            demo={demo}
            openCode={openCode}
            setOpenCode={setOpenCode}
            expandedIssueId={expandedIssueId}
            setExpandedIssueId={setExpandedIssueId}
            onOpenResolve={(issue, opts) => {
              setResolveIssue(issue);
              setResolveInitialVerb((opts && opts.initialVerb) || null);
            }}
            onResolved={onResolved}
            testMode={testMode}
            testSessionId={testSessionId}
            period={period}
            signoffKey={signoffKey}
            signoff={signoffKey && signoffs ? signoffs[signoffKey] : null}
            allowRgActions={allowRgActions}
            scope={scope}
            membershipsAllConfirmed={membershipsAllConfirmed}
            membershipCount={membershipCount}
            membershipOpenCount={membershipOpenCount}
          />
        );

        // Insert the Signer Group Review panel right after Phase 2 so
        // the partner sees Step 3 = SG Review, then Step 4 = RG, then
        // Step 5 = Standalone. The review panel hosts the SG signoff —
        // attesting it unlocks the RG phase (Step 4).
        //
        // Step 3 is gated until Step 2 is "completed" = every Phase 1
        // AND Phase 2 issue is closed. The SG signoff inside it becomes
        // available once the gate clears.
        if (p.n === 2) {
          const sgReviewGateReason = demo
            ? null
            : phaseCounts[1] > 0
              ? 'Resolve every open Step 1 issue to unlock.'
              : phaseCounts[2] > 0
                ? 'Resolve every open Step 2 issue to unlock.'
                : null;
          return (
            <div key={`phase-${p.n}-with-review`} className="cg-phase-group">
              {phaseSection}
              <SgReviewPanel
                scope={scope}
                period={period}
                email={email}
                isAdmin={isAdmin}
                demo={demo}
                testMode={testMode}
                testSessionId={testSessionId}
                stepNumber={3}
                gateReason={sgReviewGateReason}
                sgSignoff={signoffs ? signoffs.sg : null}
                onSignoffChanged={onResolved}
                onDataChanged={onResolved}
              />
            </div>
          );
        }
        // Step 4 (RG phase, p.n === 3): append the read-only "Relationship
        // Groups you're part of" section right under it, so a partner's
        // member-only RGs sit alongside the RGs they anchor/confirm.
        // Confirmation is the anchor partner's; this is for awareness +
        // moving their own signer groups out.
        if (p.n === 3) {
          // Render the membership subsection INSIDE the Step 4 card (as the
          // PhaseSection's children), not as a sibling card.
          return cloneElement(
            phaseSection,
            undefined,
            <RgMembershipSection
              rgMemberships={rgMemberships}
              email={email}
              isAdmin={isAdmin}
              // Locked behind the same gate as the rest of Step 4 — hidden
              // (read-only note) until Step 3 sign-off + Steps 1–2 clear.
              gated={gated}
              gateReason={reason}
              onResolved={onResolved}
              testMode={testMode}
              testSessionId={testSessionId}
            />
          );
        }
        return phaseSection;
      })}

      {resolveIssue && (
        <ResolveModal
          issue={resolveIssue}
          initialVerb={resolveInitialVerb}
          onClose={() => { setResolveIssue(null); setResolveInitialVerb(null); }}
          testMode={testMode}
          testSessionId={testSessionId}
          // Bubble up so the section page can refetch its issue list. RETURN the promise:
          // ResolveModal awaits it before clearing the row's pending state.
          onResolved={(result) => (onResolved ? onResolved(result) : undefined)}
          // When a verb is flagged openRowDrilldown (today:
          // split_from_group), ResolveModal closes itself and triggers
          // this callback to expand the issue row so the partner lands
          // directly on the per-member action buttons.
          onExpandRow={() => {
            setExpandedIssueId(issueSig(resolveIssue));
            // The expanded view lives under its issue_code group; make
            // sure that group is open too, otherwise the row won't be
            // visible.
            if (resolveIssue.issueCode) setOpenCode(resolveIssue.issueCode);
          }}
        />
      )}
    </div>
   </OptimisticContext.Provider>
  );
}

function PhaseSection({
  phase, displayStep, groups, phaseTotal, gated, gateReason, isEmpty,
  byCode, email, isAdmin, demo = false, openCode, setOpenCode, expandedIssueId, setExpandedIssueId,
  onOpenResolve, onResolved, testMode, testSessionId,
  period, signoffKey, signoff, allowRgActions, signoffNote = null,
  scope,
  // True when every "Relationship Groups you're part of" membership is
  // confirmed — required before Step 4 (RG) can be signed off.
  membershipsAllConfirmed = true,
  // Number of membership groups shown in this card (Step 4) — added to the
  // displayed "X open" total. NOT part of the RG sign-off math (phaseTotal).
  membershipCount = 0,
  // …of which still unconfirmed — what the "X open" pill counts.
  membershipOpenCount = 0,
  // Extra content rendered INSIDE this phase's card, after the issue list and
  // before the sign-off bar (e.g. the "Relationship Groups you're part of"
  // subsection in Step 4).
  children,
}) {

  // Paginate the open issue group's rows (default 50). openCode is panel-wide
  // (one group open at a time), so a single cursor here suffices; reset it when
  // the open group changes.
  const GROUP_PAGE_SIZE = 10;
  const [groupPage, setGroupPage] = useState(1);
  useEffect(() => { setGroupPage(1); }, [openCode]);

  // RG phase (Step 4) sign-off readiness: RG_NEEDS_CONFIRMATION rows now
  // PERSIST (reversible Confirm/Unconfirm) instead of resolving, so the
  // phase is never "empty". Sign-off becomes available once every RG row is
  // confirmed, every "Relationship Groups you're part of" membership is
  // confirmed, AND any other open RG issues (e.g. ORPHAN_CLIENT_RG) are
  // cleared. Other phases keep the plain "no open issues" rule.
  let canSignOff = isEmpty;
  if (signoffKey === 'rg') {
    const rgRows = byCode.get('RG_NEEDS_CONFIRMATION') || [];
    const nonRgOpen = phaseTotal - rgRows.length;   // ORPHAN_CLIENT_RG, etc.
    canSignOff = nonRgOpen <= 0
      && rgRows.every(i => i.confirmed)
      && membershipsAllConfirmed;
  }
  // The header's "X open" counts only what still needs the partner: confirmed RG rows
  // (they persist, with Unconfirm) and confirmed memberships are done, so after a bulk
  // confirm the pill doesn't keep reading "N open" while sign-off is already available.
  const confirmedRgRows = (byCode.get('RG_NEEDS_CONFIRMATION') || []).filter(i => i.confirmed).length;
  const openCount = phaseTotal - confirmedRgRows + membershipOpenCount;
  // Spotlight-tour anchors on the always-rendered phase headers (Step 1 / 4 / 5;
  // Step 3 is the separate SgReviewPanel). Headers render even when gated/empty,
  // so the tour can point at them regardless of the demo data state.
  const headerTour =
    phase.n === 1 ? 'cg-step-1' :
    phase.n === 3 ? 'cg-step-4' :
    phase.n === 4 ? 'cg-step-5' : undefined;
  return (
    <section className={`cg-phase ${gated ? 'cg-phase--gated' : ''} ${isEmpty ? 'cg-phase--empty' : ''}`} data-tour={headerTour}>
      <header className="cg-phase-header">
        <div className="cg-phase-step">Step {displayStep || phase.n}</div>
        <div className="cg-phase-title-block">
          <h3 className="cg-phase-title">{phase.title}</h3>
          <p className="cg-phase-subtitle">{phase.subtitle}</p>
        </div>
        <div className="cg-phase-count">
          {signoff
            ? <span className="cg-phase-pill cg-phase-pill--done">
                <i className="fas fa-check-circle" aria-hidden="true" /> {signoff.auto ? 'All reviewed' : 'Signed off'}
              </span>
            : isEmpty
              ? <span className="cg-phase-pill cg-phase-pill--done">All clear</span>
              : gated
                ? <span className="cg-phase-pill cg-phase-pill--gated">{(phaseTotal + membershipCount).toLocaleString()} · gated</span>
                : openCount <= 0
                  ? <span className="cg-phase-pill cg-phase-pill--done">All confirmed</span>
                  : <span className="cg-phase-pill cg-phase-pill--active">{openCount.toLocaleString()} open</span>}
        </div>
      </header>

      {gated && (
        <p className="cg-phase-gate-note">
          <i className="fas fa-lock" aria-hidden="true" />{' '}
          <strong>Locked.</strong> {gateReason}
          {!isEmpty && ' Issues are listed below for context, but actions are disabled until the prerequisite is met.'}
        </p>
      )}

      {!isEmpty && (
        <div
          className={`cg-groups ${gated ? 'cg-groups--gated' : ''}`}
          aria-disabled={gated ? 'true' : 'false'}
        >
          {groups.map((g, gi) => {
            // Tour: every group is expanded so each step's affected-client rows
            // + action buttons are visible (normally only one opens at a time).
            const isOpen = demo || openCode === g.issueCode;
            const rows = byCode.get(g.issueCode) || [];
            // Tour anchor: the first issue group in Step 1.
            const groupTour = phase.n === 1 && gi === 0 ? 'cg-group' : undefined;
            const totalPages = Math.max(1, Math.ceil(rows.length / GROUP_PAGE_SIZE));
            const safePage   = Math.min(groupPage, totalPages);
            const pagedRows  = rows.slice((safePage - 1) * GROUP_PAGE_SIZE, safePage * GROUP_PAGE_SIZE);
            return (
              <div key={g.issueCode} className={`cg-group ${isOpen ? 'open' : ''}`} data-tour={groupTour}>
                <button
                  type="button"
                  className="cg-group-header"
                  onClick={() => setOpenCode(isOpen ? null : g.issueCode)}
                  aria-expanded={isOpen}
                >
                  <span className={`cg-sev cg-sev-${g.severity.toLowerCase()}`}>{SEVERITY_LABEL[g.severity] || g.severity}</span>
                  <span
                    className="cg-group-label"
                    title={g.issueCode /* developer reference — surface code on hover */}
                  >
                    {g.meta.label || g.issueCode}
                  </span>
                  <span className="cg-group-count">{g.count.toLocaleString()}</span>
                  <span className="cg-group-chevron">{isOpen ? '▾' : '▸'}</span>
                </button>

                {isOpen && (
                  <div className="cg-group-body">
                    <p className="cg-group-note">{g.meta.note}</p>
                    {/* One-click review groups: confirm all of YOUR open ones at once (never on the firm-wide view). */}
                    {scope === 'mine' && (g.issueCode === 'RG_NEEDS_CONFIRMATION' || g.issueCode === 'STANDALONE_INDIVIDUAL_NEEDS_REVIEW') && (
                      <BulkConfirmBar
                        verb={g.issueCode === 'RG_NEEDS_CONFIRMATION' ? 'confirm_rg' : 'confirm_standalone'}
                        count={g.issueCode === 'RG_NEEDS_CONFIRMATION'
                          ? (rows.length >= g.count ? rows.filter(i => !i.confirmed).length : g.count)
                          : g.count}
                        period={period}
                        testMode={testMode}
                        testSessionId={testSessionId}
                        disabledReason={gated ? 'Locked until the earlier steps are done.' : ''}
                        onDone={onResolved}
                      />
                    )}
                    {rows.length === 0 ? (
                      <p className="cg-group-empty">
                        No per-issue rows loaded (count may exceed truncation cap).
                      </p>
                    ) : (
                      <table className="cg-issues-table">
                        <thead>
                          <tr>
                            <th style={{ width: 34 }}></th>
                            <th>Primary client</th>
                            <th>Key facts</th>
                            <th style={{ width: 100 }}>Partner</th>
                            <th style={{ width: 170, textAlign: 'right' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedRows.map((issue, ri) => {
                            // Stable key + expansion id (NOT issue.id, which
                            // regenerates on every refetch). Keeps the row
                            // instance — and any inline modal it hosts —
                            // mounted, and the drilldown expanded, across a
                            // diResolveAction refetch.
                            const sig = issueSig(issue);
                            return (
                            <IssueRow
                              key={sig}
                              issue={issue}
                              meta={g.meta}
                              tourAnchor={phase.n === 1 && gi === 0 && ri === 0}
                              currentEmail={email}
                              isAdmin={isAdmin}
                              expanded={expandedIssueId === sig}
                              onToggle={() => setExpandedIssueId(expandedIssueId === sig ? null : sig)}
                              onOpenResolve={onOpenResolve}
                              onResolved={onResolved}
                              testMode={testMode}
                              testSessionId={testSessionId}
                              allowRgActions={allowRgActions}
                            />
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    {totalPages > 1 && (
                      <div className="cg-pager">
                        <button type="button" disabled={safePage <= 1} onClick={() => setGroupPage(p => Math.max(1, p - 1))}>‹ Prev</button>
                        <span className="cg-pager-info">
                          {(safePage - 1) * GROUP_PAGE_SIZE + 1}–{Math.min(safePage * GROUP_PAGE_SIZE, rows.length)} of {rows.length.toLocaleString()}
                        </span>
                        <button type="button" disabled={safePage >= totalPages} onClick={() => setGroupPage(p => Math.min(totalPages, p + 1))}>Next ›</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {children}

      {/* Normally the sign-off bar is hidden while the phase is gated (an earlier
          step still has open issues). BUT if the phase is ALREADY signed off, we
          must keep the bar visible even when gated — otherwise an admin can't
          reach the Undo to re-open it, which is exactly the deadlock where a
          late change spawns a new Step-1 issue AFTER sign-off. Signing off stays
          blocked when gated (PhaseSignoffBar only shows the Undo in the
          signed-off state; `canSignOff` is false here anyway). */}
      {signoffKey && period && (!gated || signoff) && (
        <PhaseSignoffBar
          phase={signoffKey}
          period={period}
          signoff={signoff}
          canSignOff={canSignOff && !gated && !signoffNote}
          blockedNote={signoffNote}
          onChanged={onResolved}
          testMode={testMode}
          testSessionId={testSessionId}
          isAdmin={isAdmin}
        />
      )}
    </section>
  );
}

// List of Relationship Groups the caller partners a member in but does NOT
// anchor. The anchor partner owns the overall RG attestation, but each
// member partner must still agree that THEIR clients are correctly grouped
// here — that confirmation is the per-row action below. Each row also
// expands to the RgCompareCards composition, where the partner can move
// their own signer groups out if they disagree.
function RgMembershipSection({ rgMemberships, email, isAdmin, gated, gateReason, onResolved, testMode, testSessionId }) {
  const [openAnchor, setOpenAnchor] = useState(null);
  // Resolve partner emails to display names (same roster cache the "View As"
  // picker + partner cells use).
  const nameFor = useNameFor();
  // Optimistic confirm/unconfirm overrides: anchor → boolean (true = confirmed,
  // false = withdrawn). Absent = defer to the server value (rg.confirmed). The
  // effect below drops an override once the server catches up after refetch.
  const [confirmOverride, setConfirmOverride] = useState(() => new Map());
  useEffect(() => {
    setConfirmOverride(prev => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Map(prev);
      for (const rg of rgMemberships) {
        if (next.has(rg.anchorCode) && Boolean(rg.confirmed) === next.get(rg.anchorCode)) {
          next.delete(rg.anchorCode);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [rgMemberships]);
  if (!rgMemberships || rgMemberships.length === 0) return null;
  return (
    <div className="cg-rg-membership">
      <header className="cg-rg-membership-header">
        <div>
          <h4 className="cg-rg-membership-title">Relationship Groups you’re part of</h4>
          <p className="cg-rg-membership-subtitle">
            Another partner anchors each of these Relationship Groups, but you have
            clients in them. Confirm that your clients are correctly grouped here — or
            expand a group and move your signer groups elsewhere if they don’t belong.
          </p>
        </div>
        <span className="cg-phase-pill cg-phase-pill--gated">
          {rgMemberships.length} group{rgMemberships.length === 1 ? '' : 's'}
        </span>
      </header>

      {/* Locked behind the same gate as the rest of Step 4. */}
      {gated && (
        <p className="cg-phase-gate-note">
          <i className="fas fa-lock" aria-hidden="true" />{' '}
          <strong>Locked.</strong> {gateReason}
        </p>
      )}

      {!gated && (
      <div className="cg-sg-review-list">
        {rgMemberships.map(rg => {
          const isOpen = openAnchor === rg.anchorCode;
          const isConfirmed = confirmOverride.has(rg.anchorCode)
            ? confirmOverride.get(rg.anchorCode)
            : Boolean(rg.confirmed);
          return (
            <div key={rg.anchorCode} className={`cg-sg-row ${isOpen ? 'open' : ''}`}>
              {/* Header (expand toggle) on the left, the agreement control
                  right-aligned — same position as the issue table's
                  "Confirm Relationship Group" action. The confirm control is
                  a SIBLING of the header button (not nested) to keep valid
                  markup. */}
              <div className="cg-membership-row-top">
                <button
                  type="button"
                  className="cg-sg-row-header"
                  onClick={() => setOpenAnchor(isOpen ? null : rg.anchorCode)}
                  aria-expanded={isOpen}
                >
                  <span className="cg-twisty">{isOpen ? '▾' : '▸'}</span>
                  <ClientChip code={rg.anchorCode} name={rg.anchorName} />
                  <span className="cg-sg-anchor-name">
                    {rg.anchorName || (
                      <span className="cg-cell-hint"
                            title="This Relationship Group's anchor isn't in the active PE data (e.g. it was marked NLC). Expand to see its signer groups — the anchor self-heals onto a live signer group on the next refresh.">
                        (anchor inactive)
                      </span>
                    )}
                  </span>
                  <span className="cg-sg-summary">
                    {rg.sgCount != null && <>{rg.sgCount} signer group{rg.sgCount === 1 ? '' : 's'} · </>}
                    {rg.clientCount != null
                      ? `${rg.clientCount} client${rg.clientCount === 1 ? '' : 's'}`
                      : ''}
                  </span>
                  {rg.anchorPartner && (
                    <span className="cg-cell-hint" title={rg.anchorPartner}>
                      anchored by {nameFor(rg.anchorPartner)}
                    </span>
                  )}
                </button>

                <div className="cg-membership-confirm">
                  <MembershipConfirmToggle
                    anchor={rg.anchorCode}
                    confirmed={isConfirmed}
                    onOptimistic={(val) =>
                      setConfirmOverride(m => new Map(m).set(rg.anchorCode, val))}
                    onRevert={() =>
                      setConfirmOverride(m => { const n = new Map(m); n.delete(rg.anchorCode); return n; })}
                    onResolved={onResolved}
                    testMode={testMode}
                    testSessionId={testSessionId}
                  />
                </div>
              </div>

              {isOpen && (
                <div className="cg-sg-row-body">
                  {isConfirmed && (
                    <p className="cg-phase-gate-note">
                      <i className="fas fa-check-circle" aria-hidden="true" />{' '}
                      <strong>Confirmed</strong> — read-only.{' '}
                      Unconfirm above to move your signer groups.
                    </p>
                  )}
                  <RgCompareCards
                    anchorCode={rg.anchorCode}
                    // Read-only once the row is confirmed — no moving signer
                    // groups out. Still expandable to view.
                    editable={!isConfirmed}
                    viewerEmail={email}
                    isAdmin={isAdmin}
                    onResolved={onResolved}
                    testMode={testMode}
                    testSessionId={testSessionId}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

// Member-partner membership agreement toggle in "Relationship Groups you're
// part of": agree_rg_membership ⇄ unagree_rg_membership. A reversible
// attestation (never frozen by a sign-off). Optimistic: flips + toast
// immediately, reconciles in the background, reverts only if the server
// rejects it.
function MembershipConfirmToggle({ anchor, confirmed, onOptimistic, onRevert, onResolved, testMode, testSessionId }) {
  // Never disabled while saving: clicks queue (postResolveAction is serialized) and
  // reach the server in order. `inFlight` counts this toggle's POSTs so a failure
  // only reverts to the server value when nothing later is queued behind it.
  const inFlight = useRef(0);
  async function go(e) {
    e.stopPropagation();
    const next = !confirmed;
    inFlight.current += 1;
    onOptimistic(next);
    emitToast({
      kind: 'success',
      message: next ? 'Confirmed — your clients are grouped here.' : 'Confirmation removed.',
    });
    try {
      const data = await postResolveAction({
        verb: next ? 'agree_rg_membership' : 'unagree_rg_membership',
        clientCode: anchor,
        payload: {},
        testMode: Boolean(testMode),
        sessionId: testMode ? testSessionId : null,
      });
      inFlight.current -= 1;
      if (onResolved) onResolved(data);   // background reconcile, not awaited
    } catch (err) {
      inFlight.current -= 1;
      onRevert();
      emitToast({ kind: 'error', message: `Couldn't ${next ? 'confirm' : 'unconfirm'}: ${errorText(err)}. Restored.` });
      // A later click may be queued behind this one — resync from the server.
      if (inFlight.current > 0 && onResolved) onResolved();
    }
  }
  return (
    <button
      type="button"
      className={`cg-btn ${confirmed ? 'cg-btn--secondary' : 'cg-btn--primary'}`}
      onClick={go}
      title={confirmed
        ? 'You confirmed your clients are correctly grouped here. Click to unconfirm.'
        : 'Confirm your clients are correctly grouped in this Relationship Group.'}
    >
      {confirmed ? 'Unconfirm' : 'Confirm my clients are correctly grouped'}
    </button>
  );
}

// Step 4 RG row Confirm/Unconfirm toggle. A reversible attestation:
// confirm_rg records the partner's confirmation, unconfirm_rg walks it back.
// The row persists either way (no dismiss). An optimistic local override
// gives instant feedback and is reconciled by the background refetch.
// Only the anchor's partner (or an admin) may confirm: a firm-wide / office-wide viewer
// who isn't gets a read-only hint instead of a button the server would refuse.
function RgConfirmToggle({ issue, onResolved, testMode, testSessionId, canConfirm = true }) {
  const [override, setOverride] = useState(null);   // null = use server value
  const inFlight = useRef(0);   // this toggle's POSTs queued / in flight (clicks never ignored)
  const confirmed = override !== null ? override : Boolean(issue.confirmed);

  // Drop the optimistic override once the server's value catches up.
  useEffect(() => {
    if (override !== null && Boolean(issue.confirmed) === override) setOverride(null);
  }, [issue.confirmed, override]);

  async function toggle(e) {
    e.stopPropagation();
    const next = !confirmed;
    inFlight.current += 1;
    setOverride(next);
    emitToast({ kind: 'success', message: next ? 'Relationship Group confirmed.' : 'Confirmation removed.' });
    try {
      const data = await postResolveAction({
        verb:      next ? 'confirm_rg' : 'unconfirm_rg',
        issueId:   issue.id,
        issueKey:  {
          issueCode:         issue.issueCode,
          primaryClientCode: issue.primaryClientCode,
          affectedCodes:     issue.affectedCodes,
        },
        payload:   {},
        testMode:  Boolean(testMode),
        sessionId: testMode ? testSessionId : null,
      });
      inFlight.current -= 1;
      if (onResolved) onResolved(data);   // background reconcile, not awaited
    } catch (err) {
      inFlight.current -= 1;
      setOverride(null);   // revert to server truth
      emitToast({ kind: 'error', message: `Couldn't ${next ? 'confirm' : 'unconfirm'}: ${errorText(err)}.` });
      // A later click may be queued behind this one — resync from the server.
      if (inFlight.current > 0 && onResolved) onResolved();
    }
  }

  if (!canConfirm) {
    return (
      <span className="cg-cell-hint" title="Only the partner of this Relationship Group's anchor (or an admin) can confirm it.">
        <i className="fas fa-lock" aria-hidden="true" /> anchor partner confirms
      </span>
    );
  }

  return (
    <button
      type="button"
      className={`cg-btn ${confirmed ? 'cg-btn--secondary' : 'cg-btn--primary'}`}
      onClick={toggle}
      title={confirmed
        ? 'You confirmed this Relationship Group. Click to unconfirm.'
        : 'Confirm that these clients are correctly grouped together.'}
    >
      {confirmed ? 'Unconfirm' : 'Confirm'}
    </button>
  );
}

function IssueRow({ issue, meta, currentEmail, isAdmin, expanded, onToggle, onOpenResolve, onResolved, testMode, testSessionId, allowRgActions, tourAnchor }) {
  const { primaryClientCode, primaryClientName, severity, details, partnerEmail, client } = issue;
  // Anchored RG issues are confirmable only by the anchor's partner (confirm_rg refuses anyone
  // else) — a firm-wide / office-wide viewer isn't. A real admin may confirm on a partner's behalf.
  const cannotConfirmRg = issue.viewerIsAnchorPartner === false && !isAdmin;

  const keyFacts = useMemo(() => keyFactsFor(issue), [issue]);

  // Sections that use "Assign" language (no signer group yet → KNUULA_MISSING_SG):
  // the row-level "Assign Signer Group" button expands the row on first press
  // (existing behavior), and once the row is already expanded a second press
  // opens the embedded "Assign or Create New Signer Group" modal directly.
  // We bump a signal that the SplitMemberButton inside the drilldown listens
  // for (see assignOpenSignal threading below).
  const isAssignSection = issue.issueCode === 'KNUULA_MISSING_SG';
  const [assignOpenSignal, setAssignOpenSignal] = useState(0);

  // A sibling action touching one of THIS row's codes is in flight (e.g.
  // resolving client A's source-disagreement also touches the shared SG
  // code that client B's row references). Show the row in a loading /
  // disabled state instead of removing it.
  const { pendingCodes } = useOptimistic();
  const pending = Boolean(
    pendingCodes && pendingCodes.size > 0 && (
      (primaryClientCode && pendingCodes.has(primaryClientCode)) ||
      (issue.affectedCodes || []).some(c => pendingCodes.has(c))
    )
  );

  const nameFor = useNameFor();
  const myEmail = (currentEmail || '').toLowerCase();
  const pe      = (partnerEmail || '').toLowerCase();
  const partnerCell = !partnerEmail
    ? <span className="cg-unroutable" title="primary client is not in the active PE client list — admin must resolve">unroutable</span>
    : pe === myEmail
    ? <span className="cg-you">you</span>
    : <span className="cg-partner-email" title={nameFor(partnerEmail)}>{nameFor(partnerEmail)}</span>;

  // SG suggestion clusters (Phase 2 / P1S2) expand to show the CURRENT
  // signer-group structure of the suggested clients (see
  // SgSuggestionStructure) — so the partner can see whether accepting would
  // merge whole signer groups, not just the individual suggested clients.
  // Every other issue type also expands (GroupBox / ContactBlock / etc.).
  const hasDrilldown = true;

  return (
    <>
      <tr
        className={`cg-issue-row ${pending ? 'cg-issue-row--pending' : ''}`}
        onClick={hasDrilldown ? onToggle : undefined}
        style={hasDrilldown ? undefined : { cursor: 'default' }}
        aria-busy={pending ? 'true' : undefined}
      >
        <td className="cg-twisty">{hasDrilldown ? (expanded ? '▾' : '▸') : ''}</td>
        <td>
          <div className="cg-client-code"><ClientChip code={primaryClientCode} name={primaryClientName} /></div>
          <div className="cg-client-name">
            {!primaryClientName && <span className="cg-cell-hint">(not in PE)</span>}
            {client?.entityType && (
              <span className="cg-entity-pill" title="Entity type from PE">{titleCase(client.entityType)}</span>
            )}
          </div>
        </td>
        <td className="cg-key-facts">{keyFacts}</td>
        <td>{partnerCell}</td>
        <td style={{ textAlign: 'right' }}>
          {/* A sibling action on a shared code is in flight — show this row
              busy (not removed), with its own action disabled. */}
          {pending ? (
            <span className="cg-issue-pending">
              <span className="cg-sg-row-spinner" aria-hidden="true" /> Working…
            </span>
          ) : issue.issueCode === 'RG_NEEDS_CONFIRMATION' ? (
            // Step 4 RG rows persist with a reversible Confirm/Unconfirm toggle.
            <RgConfirmToggle
              issue={issue}
              onResolved={onResolved}
              testMode={testMode}
              testSessionId={testSessionId}
              canConfirm={!cannotConfirmRg}
            />
          ) : (
            <div className="cg-issue-action-buttons" data-tour={tourAnchor ? 'cg-resolve' : undefined}>
              <button
                className="cg-btn"
                disabled={cannotConfirmRg && issue.issueCode === 'RG_SUGGESTED_BY_SG'}
                onClick={(e) => {
                  e.stopPropagation();
                  // Assign sections: first press expands the row (via the
                  // openRowDrilldown verb in onOpenResolve); once expanded, a
                  // second press opens the embedded Assign modal directly.
                  if (isAssignSection && expanded) {
                    setAssignOpenSignal(s => s + 1);
                  } else {
                    onOpenResolve && onOpenResolve(issue);
                  }
                }}
                title={cannotConfirmRg && issue.issueCode === 'RG_SUGGESTED_BY_SG'
                  ? 'Only the partner of this Relationship Group\'s anchor (or an admin) can confirm it.'
                  : isAssignSection
                    ? 'Expand the row; press again to assign a Signer Group.'
                    : 'Open the Resolve panel for this issue.'}
              >
                {/* A relationship-group NLC task picks an ANCHOR, not a parent (same wording as the dialog). */}
                {issue.issueCode === 'NLC_PARENT_NEEDS_SUCCESSOR' && (issue.details || {}).layer === 'rg'
                  ? 'Choose new anchor'
                  : (meta.resolveLabel || 'Resolve')}
              </button>
              {issue.issueCode && issue.issueCode.startsWith('SG_SUGGESTED_BY_') && (
                <button
                  className="cg-btn cg-btn--secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenResolve && onOpenResolve(issue, { initialVerb: 'ignore_sg_suggestion' });
                  }}
                  title="Confirm these clients are correctly in separate Signer Groups and close this suggestion."
                >
                  Ignore proposed group
                </button>
              )}
              {(issue.issueCode === 'RG_SUGGESTED_BY_SG' || issue.issueCode === 'RG_SUGGESTED_BY_BG') && (
                <button
                  className="cg-btn cg-btn--secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenResolve && onOpenResolve(issue, { initialVerb: 'ignore_rg_suggestion' });
                  }}
                  title="These clients are correctly in their current Relationship Groups — close this suggestion."
                >
                  Ignore suggestion
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
      {expanded && hasDrilldown && (
        <tr className="cg-issue-detail">
          <td></td>
          <td colSpan={4}>
            <div className="cg-detail-grid">
              {/* SG_SOURCE_DISAGREEMENT — keep the two-column side-by-side
                  comparison; it's the right shape for "which of these two
                  groups is correct?" and GroupBox can't represent the
                  competing-parents semantic as cleanly. */}
              {issue.issueCode === 'SG_SOURCE_DISAGREEMENT' && details?.knuula_sg && details?.pe_sg ? (
                <>
                  <div className="cg-detail-full">
                    <GroupComparison
                      leftSource="knuula"
                      leftCode={details.knuula_sg}
                      rightSource={details.source === 'stored' ? 'stored' : 'pe'}
                      rightCode={details.pe_sg}
                      primaryClientCode={primaryClientCode}
                      onResolved={onResolved}
                      testMode={testMode}
                      testSessionId={testSessionId}
                      allowRgActions={allowRgActions}
                    />
                  </div>
                  {/* Fall back to a small ContactBlock for the in-question
                      client (helps partners see who they're picking for). */}
                  {client && (
                    <div className="cg-detail-full">
                      <div className="cg-detail-label">In-question client</div>
                      <ContactBlock client={client} />
                    </div>
                  )}
                </>
              ) : (issue.issueCode === 'RG_NEEDS_CONFIRMATION' || issue.issueCode === 'RG_SUGGESTED_BY_SG') && primaryClientCode ? (
                /* Phase 3 anchored: show the RG composition tree so the
                   partner sees what they're confirming. The anchor is the
                   issue's primary_client_code in both cases (RG_NEEDS_… is
                   bucketed per (anchor, partner); _BY_SG primary IS the
                   SG parent that becomes the RG anchor). */
                <div className="cg-detail-full">
                  <RgCompareCards
                    anchorCode={primaryClientCode}
                    // Editing (move a Signer Group to another RG) is offered
                    // only in the "needs partner confirmation" review. The move
                    // button is further scoped to the caller's own signer groups
                    // inside RgCompareCards.
                    editable={issue.issueCode === 'RG_NEEDS_CONFIRMATION'}
                    // The anchor partner can re-anchor the RG onto a
                    // different signer group here (server enforces authority;
                    // a viewer who isn't the anchor partner isn't offered it).
                    canReanchor={issue.issueCode === 'RG_NEEDS_CONFIRMATION' && !cannotConfirmRg}
                    viewerEmail={currentEmail}
                    isAdmin={isAdmin}
                    onResolved={onResolved}
                    testMode={testMode}
                    testSessionId={testSessionId}
                  />
                </div>
              ) : issue.issueCode === 'STANDALONE_INDIVIDUAL_NEEDS_REVIEW' ? (
                /* Step 5: read-only client view. The ONLY actions are Confirm
                   standalone / Move to an existing Relationship Group (the
                   "Review standalone" button → modal). No signer-group
                   editing, Mark NLC, or Make SG parent here — for anyone,
                   admins included — so we render a read-only ContactBlock
                   instead of the GroupBox member cards. */
                <div className="cg-detail-full">
                  <div className="cg-detail-label">Client at a glance</div>
                  {client
                    ? <ContactBlock client={client} />
                    : <span className="cg-cell-hint">(client not in active PE roster)</span>}
                </div>
              ) : issue.issueCode && issue.issueCode.startsWith('SG_SUGGESTED_BY_') ? (
                /* Step 2 suggestion: show the CURRENT signer-group structure
                   of the suggested clients (grouped, full membership) so the
                   partner sees whether accepting merges whole signer groups —
                   including clients that aren't part of the suggestion. */
                <div className="cg-detail-full">
                  <SgSuggestionStructure
                    codes={issue.affectedCodes || (issue.members || []).map(m => m.code)}
                  />
                </div>
              ) : issue.members && issue.members.length > 0 ? (
                /* Default: unified GroupBox using the same lightweight
                   .cg-compare-col chrome as SG_SOURCE_DISAGREEMENT. */
                <div className="cg-detail-full">
                  <GroupBox
                    issue={issue}
                    onResolved={onResolved}
                    testMode={testMode}
                    testSessionId={testSessionId}
                    allowRgActions={allowRgActions}
                    assignOpenSignal={assignOpenSignal}
                  />
                </div>
              ) : client ? (
                /* Fallback when no members array (back-compat with the older
                   shape): show the primary client at a glance. */
                <div className="cg-detail-full">
                  <div className="cg-detail-label">Client at a glance</div>
                  <ContactBlock client={client} />
                </div>
              ) : (
                <div>
                  <div className="cg-detail-label">Severity</div>
                  <div><span className={`cg-sev cg-sev-${severity.toLowerCase()}`}>{SEVERITY_LABEL[severity] || severity}</span></div>
                </div>
              )}

              {/* Raw JSON kept as a collapsed footnote for power-users / debugging. */}
              {details && Object.keys(details).length > 0 && (
                <details className="cg-detail-full cg-detail-raw">
                  <summary className="cg-detail-label">Raw details (advanced)</summary>
                  <pre className="cg-detail-json">{JSON.stringify(details, null, 2)}</pre>
                </details>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── GroupComparison ────────────────────────────────────────────────────────
// Lazy-loaded side-by-side member view for issue rows where two groups are in
// dispute (today: SG_SOURCE_DISAGREEMENT). Each side fetches independently
// from /api/dgGroupMembers, which serves entirely from cached PE/Knuula data.
// First expand may take a beat if the Knuula cache is cold; subsequent
// expansions during the same triage session are sub-100ms.

function GroupComparison({ leftSource, leftCode, rightSource, rightCode, primaryClientCode, onResolved, testMode, testSessionId, allowRgActions }) {
  const [left,    setLeft]    = useState(null);
  const [right,   setRight]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);

    Promise.all([
      fetch(`/api/dgGroupMembers?source=${encodeURIComponent(leftSource)}&group=${encodeURIComponent(leftCode)}`,  { credentials: 'include' }),
      fetch(`/api/dgGroupMembers?source=${encodeURIComponent(rightSource)}&group=${encodeURIComponent(rightCode)}`, { credentials: 'include' }),
    ])
      .then(async ([l, r]) => {
        if (!l.ok) throw new Error(`Knuula side: HTTP ${l.status}`);
        if (!r.ok) throw new Error(`${rightSource === 'stored' ? 'Stored' : 'PE'} side: HTTP ${r.status}`);
        const lj = await l.json();
        const rj = await r.json();
        rememberClientNames([lj, rj]);
        if (!cancelled) { setLeft(lj); setRight(rj); }
      })
      .catch(e => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [leftSource, leftCode, rightSource, rightCode]);

  if (loading) return <div className="cg-compare-loading">Loading group memberships…</div>;
  if (err)     return <div className="cg-compare-error">Error loading groups: {err}</div>;
  if (!left || !right) return null;

  return (
    <div className="cg-compare">
      <GroupColumn source={leftSource}  code={leftCode}  members={left.members}  primaryClientCode={primaryClientCode} onResolved={onResolved} testMode={testMode} testSessionId={testSessionId} allowRgActions={allowRgActions} />
      <div className="cg-compare-divider" aria-hidden="true" />
      <GroupColumn source={rightSource} code={rightCode} members={right.members} primaryClientCode={primaryClientCode} onResolved={onResolved} testMode={testMode} testSessionId={testSessionId} allowRgActions={allowRgActions} />
    </div>
  );
}

// One side of a GroupComparison. Uses the lightweight .cg-compare-col chrome
// the panel has used since inception. Members render through MemberCard so
// the cards look identical to the ones in the single-column GroupBox views.
function GroupColumn({ source, code, members, primaryClientCode, onResolved, testMode, testSessionId, allowRgActions }) {
  const sourceLabel = source === 'knuula' ? 'Knuula' : source === 'stored' ? 'Stored' : 'PE';
  return (
    <div className="cg-compare-col">
      <div className="cg-compare-header">
        <span className={`cg-source-pill cg-source-pill--${source}`}>{sourceLabel}</span>
        <span className="cg-compare-code"><ClientChip code={code} /></span>
        <span className="cg-compare-meta">
          {members.length} member{members.length === 1 ? '' : 's'}
        </span>
      </div>
      {members.length === 0 ? (
        <p className="cg-compare-empty">No members in this group.</p>
      ) : (
        <ul className="cg-compare-list">
          {members.map(m => (
            <MemberCard
              key={m.clientCode}
              member={adaptMember(m)}
              isParent={Boolean(m.isParent)}
              focus={m.clientCode === primaryClientCode}
              notInRoster={!m.inActivePeRoster}
              onResolved={onResolved}
              testMode={testMode}
              testSessionId={testSessionId}
              allowRgActions={allowRgActions}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ContactBlock({ client }) {
  const addressLine = [client.address, client.city, client.state, client.postCode]
    .filter(Boolean).join(', ');
  const tel = (client.phone || '').replace(/[^\d+]/g, '');
  return (
    <div className="cg-contact">
      <dl className="cg-contact-grid">
        {client.partner && <Row label="Client partner" value={client.partner} />}
        {client.manager && <Row label="Client manager" value={client.manager} />}
        {client.office  && <Row label="Office"         value={client.office} />}
        {client.taxpayerEmail && (
          <Row label="Taxpayer email"
               value={<a href={`mailto:${client.taxpayerEmail}`}>{client.taxpayerEmail}</a>} />
        )}
        {client.spouseEmail && (
          <Row label="Spouse email"
               value={<a href={`mailto:${client.spouseEmail}`}>{client.spouseEmail}</a>} />
        )}
        {addressLine && <Row label="Address" value={addressLine} />}
        {client.phone && (
          <Row label="Phone"
               value={tel ? <a href={`tel:${tel}`}>{client.phone}</a> : client.phone} />
        )}
        {client.peParentCode && client.peParentCode !== '' && (
          <Row label="PE parent code" value={<ClientChip code={client.peParentCode} />} />
        )}
      </dl>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function titleCase(s) {
  if (!s) return '';
  const lc = String(s).toLowerCase();
  return lc.charAt(0).toUpperCase() + lc.slice(1);
}

// ── GroupBox ────────────────────────────────────────────────────────────────
// One unified visual for "here's the group of clients we're talking about."
// Renders an SG or RG as a labeled box with member cards inside — parent
// member visually emphasized, every member showing entity_type + name +
// partner/manager/contact so the partner can judge the situation without
// leaving the row. Drives every issue type except SG_SOURCE_DISAGREEMENT
// (which keeps the existing two-column GroupComparison).

// Which member is the "parent" depends on the issue. For SG_*_MIXED the
// primary IS the SG parent. For RG suggestions the primary is the proposed
// RG anchor. For SG suggestions there's no parent yet — all peers. Etc.
function deriveParentCode(issue) {
  const code = issue.issueCode;
  const d    = issue.details || {};
  if (code === 'SG_BUSINESS_INDIVIDUAL_MIXED' ||
      code === 'SG_TRUST_ESTATE_MIXED' ||
      code === 'SG_NON_SMLLC_MIXED' ||
      code === 'SG_MULTI_INDIVIDUAL_PARENT') {
    return d.sg_group_id || issue.primaryClientCode;
  }
  if (code === 'RG_SUGGESTED_BY_SG' || code === 'RG_SUGGESTED_BY_BG') {
    return issue.primaryClientCode;
  }
  if (code === 'SG_NEEDS_CONFIRMATION') {
    return d.suggested_sg || d.knuula_sg || d.pe_sg || null;
  }
  if (code === 'NLC_PARENT_NEEDS_SUCCESSOR') {
    // The NLC client is the (gone) parent; primary_client_code is a remaining member.
    return d.nlcClientCode || null;
  }
  if (code === 'SG_PARENT_INACTIVE_IN_PE') {
    // primary_client_code IS the dead parent — show it in the header so the
    // partner sees which SG is orphaned. Members list will only contain the
    // still-active children (parent itself is filtered out as inactive).
    return d.dead_parent || issue.primaryClientCode;
  }
  return null;
}

// Plain-English explanation of what the partner is looking at. Prefers the
// rule_note authored server-side when available; falls back to per-issue-code
// composed text. Rendered inside the GroupBox so the partner doesn't have
// to look away from the member cards to remember why a group is flagged.
function deriveRuleNote(issue) {
  const code = issue.issueCode;
  const d    = issue.details || {};
  // (SG_PARENT_INACTIVE_IN_PE's stored rule_note still says "Pick a new parent" — the fix is the
  // per-member "Make SG Parent" button, which the composed text below names.)
  if (d.rule_note && code !== 'SG_PARENT_INACTIVE_IN_PE') return d.rule_note;
  switch (code) {
    case 'SG_BUSINESS_INDIVIDUAL_MIXED':
      return "A Business (LLC, LLP, Sole Proprietor or Partnership) is grouped with an Individual / Estate / Trust, or an Estate / Trust is grouped with an Individual. A Business is only legitimate here when it is a single-member LLC on that personal return; an Estate / Trust may join an Individual when it truly belongs on that return. Confirm or split. Splitting only changes the Signer Group — you can still group them together at the Relationship Group level in Step 4.";
    case 'SG_NON_SMLLC_MIXED':
      return "Only an LLC, LLP, Sole Proprietor or Partnership can be a single-member LLC on a personal return. A business of any other type (S-Corp, C Corp, Corporation, Non-Profit, …) cannot share a signer group with an Individual, Estate or Trust. Split it out into its own signer group, or one with other businesses — there is nothing to confirm.";
    case 'SG_TRUST_ESTATE_MIXED':
      return "A group with an Individual may hold Estate clients or Trust clients, but not both kinds. Split the Estate(s) or the Trust(s) out into their own signer group — there is nothing to confirm.";
    case 'SG_MULTI_INDIVIDUAL_PARENT':
      return "An Individual under an Individual parent is only legitimate for a dependent minor. Confirm dependent or split.";
    case 'SG_NEEDS_CONFIRMATION':
      return "A Signer Group is suggested for this client (Knuula preferred), but no partner has confirmed it yet.";
    case 'KNUULA_MISSING_SG':
      return "This client has no Signer Group on file in Knuula — likely never received a letter. Assign one or mark standalone.";
    case 'ORPHAN_CLIENT_RG':
      return "This client has no Relationship Group yet. Needs a Relationship Group assignment.";
    case 'NLC_PARENT_NEEDS_SUCCESSOR':
      return d.layer === 'rg'
        ? `${d.nlcClientCode} was marked No Longer Client and anchored this Relationship Group, so its remaining Signer Groups have no anchor. Choose which Signer Group anchors the group now, disband it, or merge it into another.`
        : `${d.nlcClientCode} was marked No Longer Client and was the Signer Group parent, so the clients below have no parent. Choose who leads the group now, disband it, or move it into another Signer Group.`;
    case 'SG_PARENT_INACTIVE_IN_PE':
      return "Knuula still lists this Signer Group parent, but that client is no longer in the active PE roster. Use “Make SG Parent” on the member below who should lead the group so the remaining active members have a valid Signer Group.";
    case 'SG_SUGGESTED_BY_EMAIL':
      return `These clients share the email ${d.matchValue || '(unknown)'}. Consider unifying their Signer Group.`;
    case 'SG_SUGGESTED_BY_PHONE':
      return `These clients share the phone number ${d.matchValue || '(unknown)'}. Consider unifying their Signer Group.`;
    case 'SG_SUGGESTED_BY_ADDRESS':
      return `These clients share an address (${d.matchValue || '—'}). Consider unifying their Signer Group.`;
    case 'SG_SUGGESTED_BY_CLIENT_CODE':
      return `These clients share the client-code prefix ${d.matchValue || '—'}. Common when related entities are assigned sub-accounts of one parent.`;
    case 'RG_SUGGESTED_BY_SG':
      return `These clients are in the same Signer Group (${d.matchValue || '—'}) but have different or missing Relationship Group assignments.`;
    case 'RG_SUGGESTED_BY_BG':
      return `These clients are in the same billing group (${d.matchValue || '—'}) but have different or missing Relationship Group assignments.`;
    default:
      return null;
  }
}

// Tint for the rule-note strip inside a GroupBox:
//   warn — true rule violations  (SG_*_MIXED, MULTI_INDIVIDUAL_PARENT)
//   info — suggestions / soft prompts  (*_SUGGESTED_*, NEEDS_CONFIRMATION)
//   note — neutral instruction  (KNUULA_MISSING_SG, ORPHAN_CLIENT_RG)
function ruleNoteKind(issueCode) {
  if (issueCode === 'SG_BUSINESS_INDIVIDUAL_MIXED' ||
      issueCode === 'SG_TRUST_ESTATE_MIXED'        ||
      issueCode === 'SG_NON_SMLLC_MIXED'           ||
      issueCode === 'SG_MULTI_INDIVIDUAL_PARENT'   ||
      issueCode === 'SG_PARENT_INACTIVE_IN_PE'     ||
      issueCode === 'NLC_PARENT_NEEDS_SUCCESSOR') {
    return 'warn';
  }
  if (issueCode.includes('SUGGESTED') || issueCode === 'SG_NEEDS_CONFIRMATION') {
    return 'info';
  }
  return 'note';
}

// Map entity_type → kind bucket ('IND' | 'TE' | 'BIZ' | 'OBZ' | 'UNK'). Mirrors the
// backend KIND_CASE in clientGroupsIngest.js so the UI and the rules agree on
// what counts as which kind. BIZ = a business that can be a SMLLC; OBZ = any other business.
function entityKindOf(entityType) {
  return kindOf(entityType);
}

// Map entity_type → kind bucket for chip color.
function entityKindClass(entityType) {
  const kind = entityKindOf(entityType);
  if (kind === 'IND') return 'cg-entity-pill--ind';
  if (kind === 'TE')  return 'cg-entity-pill--te';
  if (kind === 'UNK') return 'cg-entity-pill--unk';
  return 'cg-entity-pill--biz';   // BIZ and OBZ share the business tint
}

// Which member(s) in a mixed-type group are "causing" the violation — the ones
// to badge so the partner can see exactly who to split out. Today only the
// Business+Individual rule is badged (flag the Individuals, per the rule that a
// Business may only sit with an Individual when it's an SMLLC on that
// Individual's return). Returns a tooltip string, or null if not flagged.
function mixViolationTip(issue, member) {
  const kind = entityKindOf(member.entityType);
  const isPersonal = kind === 'IND' || kind === 'TE';
  if (issue.issueCode === 'SG_BUSINESS_INDIVIDUAL_MIXED' && isPersonal) {
    return 'This personal return (Individual / Estate / Trust) is grouped with a '
         + 'Business, or an Estate / Trust is grouped with an Individual. Split it '
         + 'into its own Signer Group, or confirm the grouping is right.';
  }
  if (issue.issueCode === 'SG_NON_SMLLC_MIXED' && kind === 'OBZ') {
    return 'Only an LLC, LLP, Sole Proprietor or Partnership can be a single-member LLC, so this '
         + 'business cannot share a Signer Group with an Individual / Estate / Trust. Split it out.';
  }
  if (issue.issueCode === 'SG_TRUST_ESTATE_MIXED' && kind === 'TE') {
    return 'This group has both Estate and Trust clients alongside an Individual. '
         + 'Split the Estate(s) or the Trust(s) out into their own Signer Group.';
  }
  return null;
}

// GroupBox: single-column variant of the same chrome SG_SOURCE_DISAGREEMENT
// has always used. Renders one .cg-compare-col with a kind pill (SG / RG /
// Possible SG / Possible RG), the parent code, member count, and the
// MemberCard list. No teal banner, no in-expansion action buttons — those
// belong on the main row's ACTION column for consistency with the other
// issue types. The rule explanation is already shown above the expansion
// via meta.note (see PhaseSection's per-issue subtitle).
function GroupBox({ issue, onResolved, testMode, testSessionId, allowRgActions, assignOpenSignal }) {
  const parentCode   = deriveParentCode(issue);
  const isRgIssue    = issue.issueCode.startsWith('RG_') || issue.issueCode === 'ORPHAN_CLIENT_RG' ||
                       (issue.issueCode === 'NLC_PARENT_NEEDS_SUCCESSOR' && (issue.details || {}).layer === 'rg');
  const isSuggestion = issue.issueCode.includes('SUGGESTED');
  const kindLabel    = isSuggestion
    ? (isRgIssue ? 'Possible Relationship Group' : 'Possible Signer Group')
    : (isRgIssue ? 'Relationship Group' : 'Signer Group');
  const sourceClass  = isSuggestion ? 'cg-source-pill--suggest' : 'cg-source-pill--group';
  const members      = issue.members || [];

  // Optimistic per-member removal: when a member's move/NLC is in flight
  // (markPending(code) fired on submit), drop it from the group list right
  // away so the partner sees it leave immediately — instead of waiting for
  // the refetch. clearPending on failure (or after the refetch settles)
  // brings it back / reconciles it, so a failed move reverts cleanly. The
  // issue row itself stays put (its identity is the anchor, not the member
  // set), so a sibling member's open modal isn't disturbed.
  const { pendingCodes } = useOptimistic();

  // Parent first, rest by code.
  const sorted = [...members]
    .filter(m => !(pendingCodes && pendingCodes.has(m.code)))
    .sort((a, b) => {
      if (a.code === parentCode) return -1;
      if (b.code === parentCode) return 1;
      return a.code.localeCompare(b.code);
    });

  const ruleNote = deriveRuleNote(issue);
  const noteKind = ruleNoteKind(issue.issueCode);

  // Member actions never optimistically dismiss the whole issue row — they
  // keep it visible in the loading state and let the refetch reconcile what
  // remains. A single-member action frequently does NOT resolve the issue:
  //   • removing one of several individuals leaves the rest still mixed;
  //   • switching the SG parent ("promote") changes nothing about the
  //     entity-type mix — the issue is unaddressed.
  // Optimistically removing the row in those cases falsely signals "resolved"
  // (the bugs the partners hit). The backend rebuild is the source of truth:
  // if the action genuinely cleared the issue it won't be re-detected and the
  // row disappears on refetch; otherwise it stays. The brief loading state is
  // the honest representation of "working — see what's left."
  const keepGroupVisible = true;

  return (
    <div className="cg-compare-col">
      <div className="cg-compare-header">
        <span className={`cg-source-pill ${sourceClass}`}>{kindLabel}</span>
        {parentCode
          ? <span className="cg-compare-code"><ClientChip code={parentCode} /></span>
          : <span className="cg-compare-code"><code className="cg-code-proposed">(proposed)</code></span>}
        {issue.issueCode === 'SG_PARENT_INACTIVE_IN_PE' && (
          <span className="cg-compare-tag cg-compare-tag--off">parent inactive in PE</span>
        )}
        {issue.issueCode === 'NLC_PARENT_NEEDS_SUCCESSOR' && (
          <span className="cg-compare-tag cg-compare-tag--off">
            {(issue.details || {}).layer === 'rg' ? 'anchor' : 'parent'} marked No Longer Client
          </span>
        )}
        <span className="cg-compare-meta">
          {sorted.length} member{sorted.length === 1 ? '' : 's'}
        </span>
      </div>
      {sorted.length === 0 ? (
        <p className="cg-compare-empty">No member detail available (off-roster).</p>
      ) : (
        <ul className="cg-compare-list">
          {sorted.map(m => (
            <MemberCard
              key={m.code}
              member={m}
              isParent={m.code === parentCode}
              notInRoster={m.inActivePeRoster === false}
              onResolved={onResolved}
              testMode={testMode}
              testSessionId={testSessionId}
              allowRgActions={allowRgActions}
              // Warning badge on the member(s) causing a mixed-type violation
              // (today: the Individuals in a Business+Individual group).
              violationTip={mixViolationTip(issue, m)}
              // No signer group exists yet for a Knuula-missing-SG client, so
              // "Make SG Parent" is meaningless here, and the SG action is an
              // ASSIGNMENT (not a move) — swap that verbiage too.
              hidePromote={issue.issueCode === 'KNUULA_MISSING_SG'}
              // A relationship-group row (RG chip) must not offer signer-group buttons: RG edits are made at the
              // signer-group level, via the row's action, not by re-parenting one client. Mark NLC stays.
              rgContext={isRgIssue}
              assignMode={issue.issueCode === 'KNUULA_MISSING_SG'}
              // External open trigger from the row-level "Assign Signer Group"
              // button when the row is already expanded.
              assignOpenSignal={assignOpenSignal}
              // Multi-member group: keep the row visible (loading) on a
              // single-member action instead of dismissing the whole issue.
              keepGroupVisible={keepGroupVisible}
            />
          ))}
        </ul>
      )}
      {ruleNote && (
        <div className={`cg-rule-note cg-rule-note--${noteKind}`}>
          <span className="cg-rule-note-icon" aria-hidden="true">
            {noteKind === 'warn' ? '⚠' : noteKind === 'info' ? 'ⓘ' : '·'}
          </span>
          <span className="cg-rule-note-text">{ruleNote}</span>
        </div>
      )}
    </div>
  );
}

// MemberCard renders one client inside a GroupBox (single-column) or a
// GroupColumn (comparison-side). Uses the existing lightweight
// .cg-compare-member chrome that SG_SOURCE_DISAGREEMENT has used since
// inception, so every issue type now shares the same visual rhythm.
//
// Three layered emphasis states:
//   isParent    — SG/RG parent (subtle blue left-border + 'parent' tag)
//   focus       — in-question client for this issue row (gold border + tag)
//   notInRoster — code exists in Knuula but not in active PE list (dimmed)
export function MemberCard({ member, isParent, focus, notInRoster, onResolved, testMode, testSessionId, allowRgActions, hidePromote, keepGroupVisible, assignMode, assignOpenSignal, violationTip, rgContext = false, soloGroup = false }) {
  const nameFor = useNameFor();
  const { email: viewerEmail, isAdmin, impersonating } = useAuth();
  const demo = useTourDemo();
  // Client Groups' mark_nlc is the client's Partner or an UNIMPERSONATED admin only (a Manager's
  // request goes through the other sections' NLC switch) — anyone else gets a 403, so don't
  // offer the button. The Partner is judged from the member's PE partner e-mail against the
  // effective identity, exactly as the server does; a member with no partner e-mail on file
  // has no Partner to match, so only an admin sees the button there.
  const mayMarkNlc = demo
    || (isAdmin && !impersonating)
    || Boolean(member.partnerEmail && viewerEmail
         && String(member.partnerEmail).toLowerCase().trim() === String(viewerEmail).toLowerCase().trim());
  const addressLine = [member.address, member.city, member.state, member.postCode]
    .filter(Boolean).join(', ');
  const hasContact  = member.partner || member.manager || member.office ||
                      member.taxpayerEmail || addressLine;
  // Per-member actions are meaningful only for codes still in the active
  // PE roster — server handlers reject otherwise.
  const showMemberActions = !notInRoster && member.code;
  // SG-editing buttons (Split / Make SG Parent / Mark NLC). There are NO
  // per-member RG actions: in Step 4 all Relationship-Group edits happen at
  // the signer-group level (the SG cards in RgCompareCards), so a single
  // client can never be split out of its signer group's RG.
  const showSgActions = showMemberActions;
  // Inside a relationship-group row the signer-group composition buttons (Change signer group / Make SG parent) are
  // not offered: those edit a SIGNER group, and the row is about a RELATIONSHIP group. Mark NLC is unchanged.
  // Step 3 (soloGroup): a client alone in its own Signer Group has nothing to split out of and no group to
  // create (QA, 2026-11-03), so the Change / Create button is not offered. (Join an existing group from Step 2.)
  const showSgComposition = showSgActions && !rgContext && !soloGroup;
  const showAnyActions = showSgActions;
  return (
    <li className={[
      'cg-compare-member',
      isParent    ? 'cg-compare-member--parent'     : '',
      focus       ? 'cg-compare-member--focus'      : '',
      notInRoster ? 'cg-compare-member--off-roster' : '',
    ].filter(Boolean).join(' ')}>
      {/* Left column: code line + name + contact stacked. Right column:
          actions — vertically centered against the full card height via
          the flex layout on .cg-compare-member. */}
      <div className="cg-compare-main">
        <div className="cg-compare-line">
          <ClientChip code={member.code} name={member.name} />
          {isParent    && <span className="cg-compare-tag">{rgContext ? 'anchor' : 'parent'}</span>}
          {focus       && <span className="cg-compare-tag cg-compare-tag--focus">in question</span>}
          {notInRoster && <span className="cg-compare-tag cg-compare-tag--off">not in PE</span>}
          {member.entityType && (
            <span className={`cg-entity-pill ${entityKindClass(member.entityType)}`}>
              {titleCase(member.entityType)}
            </span>
          )}
          {violationTip && (
            <span className="cg-member-warn" title={violationTip}>
              <i className="fas fa-exclamation-triangle" aria-hidden="true" /> causing the mix
            </span>
          )}
        </div>
        {!member.name && (
          <div className="cg-compare-name"><span className="cg-cell-hint">(unknown — not in active PE roster)</span></div>
        )}
        {hasContact && (
          <div className="cg-compare-contact">
            {member.partner       && <span className="cg-compare-contact-item" title={nameFor(member.partner)}>CP: {nameFor(member.partner)}</span>}
            {member.manager       && <span className="cg-compare-contact-item" title={nameFor(member.manager)}>Mgr: {nameFor(member.manager)}</span>}
            {member.office        && <span className="cg-compare-contact-item">{member.office}</span>}
            {member.taxpayerEmail && <span className="cg-compare-contact-item">{member.taxpayerEmail}</span>}
            {addressLine          && <span className="cg-compare-contact-item">{addressLine}</span>}
          </div>
        )}
      </div>
      {showAnyActions && (
        <span className="cg-member-actions">
          {/* SG-editing actions. */}
          {showSgComposition && (
            <SplitMemberButton
              member={member}
              onResolved={onResolved}
              testMode={testMode}
              testSessionId={testSessionId}
              keepGroupVisible={keepGroupVisible}
              assignMode={assignMode}
              openSignal={assignOpenSignal}
            />
          )}
          {/* Promote-to-SG-parent is meaningless when the client already IS
              the SG parent, or when there's no signer group at all (Knuula
              missing SG → hidePromote). */}
          {showSgComposition && !isParent && !hidePromote && (
            <PromoteSgParentButton
              member={member}
              onResolved={onResolved}
              testMode={testMode}
              testSessionId={testSessionId}
              keepGroupVisible={keepGroupVisible}
            />
          )}
          {/* No per-member Relationship-Group action: RG moves are done at
              the signer-group level in Step 4 (RgCompareCards SG cards), so
              a client can't be split out of its signer group's RG. */}
          {/* Mark NLC changes SG composition, and the server refuses anyone but
              the client's Partner (or an unimpersonated admin) — see mayMarkNlc. */}
          {showSgActions && mayMarkNlc && (
            <MarkNlcButton
              member={member}
              onResolved={onResolved}
              testMode={testMode}
              testSessionId={testSessionId}
              keepGroupVisible={keepGroupVisible}
            />
          )}
        </span>
      )}
    </li>
  );
}

// Adapt the dgGroupMembers payload (clientCode / clientName / clientPartner)
// to the MemberCard shape (code / name / partner). Lets us use one consistent
// rendering across both data sources.
function adaptMember(m) {
  return {
    code:          m.clientCode,
    name:          m.clientName,
    entityType:    m.entityType,
    partner:       m.clientPartner,
    partnerEmail:  m.partnerEmail || null,   // who may Mark NLC (MemberCard)
    manager:      m.clientManager  || null,   // dgGroupMembers may not carry these
    office:        m.clientOffice   || null,
    taxpayerEmail: m.taxpayerEmail,
    spouseEmail:   m.spouseEmail,
    address:       m.address,
    city:          m.city,
    state:         m.state,
    postCode:      m.postCode,
    phone:         m.phone,
  };
}

// SgSuggestionStructure: the Step-2 suggestion drilldown. Lazy-fetches
// /api/dgSgStructure for the suggested client codes and renders each client's
// CURRENT signer group — grouped, with full membership — so the partner sees
// exactly which signer groups an Accept would pull together. Three member tags:
//   • "in suggestion"      — a cluster client that's a parent/standalone → an
//                            eligible pick for the unified group's new parent
//   • "comes along"        — a cluster client that's a CHILD of an existing
//                            group → it gets regrouped under the chosen parent,
//                            but can't itself be the parent
//   • "also in this group" — NOT part of the suggestion → context only; it
//                            stays where it is (won't move)
// Read-only: all structural edits stay in the Signer Group Review step.
function SgSuggestionStructure({ codes }) {
  const codesKey = (codes || []).join(',');
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!codesKey) {
      setState({ loading: false, error: null, data: { groups: [], sgCount: 0, clientCount: 0 } });
      return undefined;
    }
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    fetch(`/api/dgSgStructure?codes=${encodeURIComponent(codesKey)}`, { credentials: 'include' })
      .then(async (r) => { if (!r.ok) throw new Error(await r.text() || `HTTP ${r.status}`); return r.json(); })
      .then((j) => { rememberClientNames(j); if (!cancelled) setState({ loading: false, error: null, data: j }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, error: e.message, data: null }); });
    return () => { cancelled = true; };
  }, [codesKey]);

  if (state.loading) return <p className="cg-compare-empty">Loading current signer-group structure…</p>;
  if (state.error)   return <p className="cg-compare-empty">Couldn’t load structure: {state.error}</p>;

  const data   = state.data || { groups: [] };
  const groups = data.groups || [];
  if (groups.length === 0) {
    return <p className="cg-compare-empty">No current signer-group data for these clients.</p>;
  }

  return (
    <div className="cg-sg-structure">
      <div className="cg-detail-label">Current signer groups of the suggested clients</div>
      <p className="cg-sg-structure-summary">
        These clients currently sit across <strong>{data.sgCount}</strong> signer
        group{data.sgCount === 1 ? '' : 's'} ({data.clientCount} client{data.clientCount === 1 ? '' : 's'} total).{' '}
        Accepting unifies the suggested clients under one chosen parent. Rows marked{' '}
        <em>also in this group</em> aren’t part of the suggestion and won’t move.
      </p>
      <div className="cg-sg-structure-groups">
        {groups.map(g => (
          <div key={g.sgParentCode} className="cg-compare-col">
            <div className="cg-compare-header">
              <span className={`cg-source-pill ${g.standalone ? 'cg-source-pill--suggest' : 'cg-source-pill--group'}`}>
                {g.standalone ? 'No signer group' : 'Signer Group'}
              </span>
              <span className="cg-compare-code"><ClientChip code={g.sgParentCode} name={g.sgParentName} /></span>
              <span className="cg-compare-meta">
                {g.members.length} member{g.members.length === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="cg-compare-list">
              {g.members.map(m => (
                <li
                  key={m.code}
                  className={`cg-compare-member ${m.inSuggestion ? 'cg-compare-member--focus' : ''}`}
                >
                  <div className="cg-compare-main">
                    <div className="cg-compare-line">
                      <ClientChip code={m.code} name={m.name} />
                      {m.isParent && <span className="cg-compare-tag">parent</span>}
                      {/* Three states:
                          • in suggestion — cluster client that's a parent/standalone → an eligible new-parent pick
                          • comes along    — cluster client that's a child → gets regrouped under the chosen parent, but can't BE the parent
                          • also in this group — not part of the suggestion → context only, won't move */}
                      {!m.inSuggestion ? (
                        <span className="cg-compare-tag cg-compare-tag--muted"
                              title="Not part of this suggestion — shown for context. It won't move.">
                          also in this group
                        </span>
                      ) : m.isParent ? (
                        <span className="cg-compare-tag cg-compare-tag--focus">in suggestion</span>
                      ) : (
                        <span className="cg-compare-tag cg-compare-tag--off"
                              title="Part of the suggestion — it'll be regrouped under the chosen parent, but can't be picked as the parent (it's currently a child of an existing Signer Group).">
                          comes along
                        </span>
                      )}
                      {m.entityType && (
                        <span className={`cg-entity-pill ${entityKindClass(m.entityType)}`}>
                          {titleCase(m.entityType)}
                        </span>
                      )}
                    </div>
                    {!m.name && <div className="cg-compare-name"><span className="cg-cell-hint">(unknown)</span></div>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// Per-issue one-liner. Each code extracts the most useful fields from
// `details` so partners can make a call without expanding.
// Render a client code alongside the client name (when known) so partners
// can read facts without needing to look codes up. Names come from
// issue.members, which dgGroupsDQ attaches per code in affected_codes.
// NB: the member shape uses `code` and `name` (not `clientCode` / `clientName`)
// — see memberFromPe in api/dgGroupsDQ/index.js.
function CodeWithName({ code, members }) {
  if (!code) return <span className="cg-cell-hint">—</span>;
  const name = (members || []).find(m => m.code === code)?.name;
  return <ClientChip code={code} name={name} />;
}

// Layout helpers: vertical key-facts block with labels on their own line
// and tab-indented values below each. Keeps each client on its own line so
// partners can scan codes + names without parsing comma-separated runs.
function FactLabel({ children }) {
  return <div className="cg-key-fact-label">{children}</div>;
}
function FactValue({ children }) {
  return <div className="cg-key-fact-value">{children}</div>;
}

// Compact list of cohort members rendered under a Knuula/PE anchor in
// the SG_SOURCE_DISAGREEMENT Key Facts. Capped at 6 entries so a
// massive group doesn't blow out the table row height; surplus shown
// as "(and N more)". Members are sorted by client_code on the server.
function CohortPreview({ members, anchor }) {
  if (!members || members.length === 0) return null;
  const CAP = 6;
  const visible = members.slice(0, CAP);
  const overflow = members.length - visible.length;
  return (
    <ul className="cg-cohort-preview">
      {visible.map(m => {
        const isAnchor = m.clientCode === anchor;
        return (
          <li key={m.clientCode} className={isAnchor ? 'cg-cohort-preview-anchor' : ''}>
            <ClientChip code={m.clientCode} name={m.clientName} />
            {isAnchor && <span className="cg-cohort-preview-parent-tag">parent</span>}
          </li>
        );
      })}
      {overflow > 0 && (
        <li className="cg-cohort-preview-overflow">(and {overflow} more)</li>
      )}
    </ul>
  );
}

function keyFactsFor(issue) {
  const d = issue.details || {};
  const members = issue.members || [];
  switch (issue.issueCode) {
    case 'SG_SOURCE_DISAGREEMENT': {
      // Suggested === Knuula for this rule (see rebuildDqIssues), so we
      // mark the Knuula row with a corner badge instead of repeating the
      // value as its own fact line. Only show the badge when the rule's
      // suggestion actually aligns with the Knuula value — defensive
      // against any future change to how suggested_sg is computed.
      const knuulaIsSuggested = d.suggested_sg && d.suggested_sg === d.knuula_sg;
      const peIsSuggested     = d.suggested_sg && d.suggested_sg === d.pe_sg;
      // d.knuula_members / d.pe_members are nested JSON arrays surfaced
      // by rebuildDqIssues — list every client in each competing
      // cohort so the partner can compare at-a-glance without
      // expanding the row. Defensive on missing values for older rows
      // that ran under the prior SQL (no members included).
      const knuulaMembers = Array.isArray(d.knuula_members) ? d.knuula_members : [];
      const peMembers     = Array.isArray(d.pe_members)     ? d.pe_members     : [];
      return (
        <div className="cg-key-facts-block">
          <FactLabel>Knuula:</FactLabel>
          <FactValue>
            <span className="cg-fact-with-badge">
              <CodeWithName code={d.knuula_sg} members={members} />
              {knuulaIsSuggested && <span className="cg-fact-badge">Suggested</span>}
            </span>
            <CohortPreview members={knuulaMembers} anchor={d.knuula_sg} />
          </FactValue>
          <FactLabel>{d.source === 'stored' ? 'Stored (on file):' : 'PE:'}</FactLabel>
          <FactValue>
            <span className="cg-fact-with-badge">
              <CodeWithName code={d.pe_sg} members={members} />
              {peIsSuggested && <span className="cg-fact-badge">Suggested</span>}
            </span>
            <CohortPreview members={peMembers} anchor={d.pe_sg} />
          </FactValue>
        </div>
      );
    }
    case 'SG_NEEDS_CONFIRMATION':
      return (
        <div className="cg-key-facts-block">
          <FactLabel>Suggested:</FactLabel>
          <FactValue>
            <CodeWithName code={d.suggested_sg} members={members} />
            {d.knuula_sg && d.pe_sg && d.knuula_sg === d.pe_sg && (
              <span className="cg-cell-hint"> (Knuula and PE agree)</span>
            )}
          </FactValue>
        </div>
      );
    case 'KNUULA_MISSING_SG':
      return (
        <div className="cg-key-facts-block">
          <FactLabel>PE parent:</FactLabel>
          <FactValue><CodeWithName code={d.pe_parent_code} members={members} /></FactValue>
          <div className="cg-cell-hint">No Knuula letter on record.</div>
        </div>
      );
    case 'ORPHAN_CLIENT_RG':
      return <>No Relationship Group on file for this client.</>;
    case 'SG_PARENT_INACTIVE_IN_PE': {
      // primary_client_code IS the dead parent; the members are the still-active children.
      const deadParent = d.dead_parent || issue.primaryClientCode;
      const kids = d.active_child_count != null ? d.active_child_count : (issue.affectedCodes || []).length;
      return (
        <div className="cg-key-facts-block">
          <FactLabel>Parent no longer in PE:</FactLabel>
          <FactValue><ClientChip code={deadParent} /></FactValue>
          <FactLabel>Active members left:</FactLabel>
          <FactValue>{Number(kids).toLocaleString()} client{kids === 1 ? '' : 's'}</FactValue>
          <div className="cg-cell-hint">Expand the row and use “Make SG Parent” on the new leader.</div>
        </div>
      );
    }
    case 'STANDALONE_INDIVIDUAL_NEEDS_REVIEW': {
      // Server details: { client_code, client_name, entity_type }; PE contact info rides on issue.client.
      const who = issue.client || {};
      return (
        <div className="cg-key-facts-block">
          <FactLabel>Relationship group:</FactLabel>
          <FactValue>1 signer group · 1 client (alone)</FactValue>
          {(d.entity_type || who.entityType) && (
            <>
              <FactLabel>Entity type:</FactLabel>
              <FactValue>{titleCase(d.entity_type || who.entityType)}</FactValue>
            </>
          )}
          {who.office && (
            <>
              <FactLabel>Office:</FactLabel>
              <FactValue>{who.office}</FactValue>
            </>
          )}
        </div>
      );
    }
    case 'NLC_PARENT_NEEDS_SUCCESSOR': {
      const isRg  = d.layer === 'rg';
      const total = d.memberCount || (issue.affectedCodes || []).length;
      const mine  = d.partnerMemberCount || (issue.affectedCodes || []).length;
      return (
        <div className="cg-key-facts-block">
          <FactLabel>{isRg ? 'Relationship Group anchor:' : 'Signer Group parent:'}</FactLabel>
          <FactValue>
            <ClientChip code={d.nlcClientCode} name={d.nlcClientName} />
            <span className="cg-cell-hint"> — marked No Longer Client</span>
          </FactValue>
          <FactLabel>{isRg ? 'Left without an anchor:' : 'Left without a parent:'}</FactLabel>
          <FactValue>
            {total.toLocaleString()} client{total === 1 ? '' : 's'}
            {mine !== total ? <span className="cg-cell-hint"> ({mine.toLocaleString()} yours)</span> : null}
          </FactValue>
          {isRg && d.sgElectionPending && (
            <div className="cg-cell-hint">Choose the Signer Group parent first.</div>
          )}
        </div>
      );
    }
    case 'RG_NEEDS_CONFIRMATION':
    case 'RG_SUGGESTED_BY_SG': {
      // Counts come from dgGroupsDQ, which attaches the full RG composition
      // (same numbers RgCompareCards shows on expansion) for anchored RG
      // issues. Null when the anchor has no established RG yet — fall back
      // to the affected-cluster size so the cell still says something.
      const sgCount     = issue.rgSgCount;
      const clientCount = issue.rgClientCount != null ? issue.rgClientCount : (issue.members || []).length;
      return (
        <div className="cg-key-facts-block">
          <FactLabel>Relationship group:</FactLabel>
          <FactValue>
            {sgCount != null && (
              <>{sgCount.toLocaleString()} signer group{sgCount === 1 ? '' : 's'} · </>
            )}
            {clientCount.toLocaleString()} client{clientCount === 1 ? '' : 's'}
          </FactValue>
        </div>
      );
    }
    case 'SG_TRUST_ESTATE_MIXED':
    case 'SG_NON_SMLLC_MIXED':
    case 'SG_BUSINESS_INDIVIDUAL_MIXED':
    case 'SG_MULTI_INDIVIDUAL_PARENT':
      return (
        <div className="cg-key-facts-block">
          <FactLabel>Group:</FactLabel>
          <FactValue>
            <CodeWithName code={d.sg_group_id || issue.primaryClientCode} members={members} />
          </FactValue>
          <FactLabel>Members:</FactLabel>
          {members.length > 0 ? (
            members.map(m => (
              <FactValue key={m.code}>
                <ClientChip code={m.code} name={m.name} />
                {m.entityType ? <span className="cg-cell-hint"> ({m.entityType})</span> : null}
              </FactValue>
            ))
          ) : (
            <FactValue>{d.members_summary || '—'}</FactValue>
          )}
        </div>
      );
    case 'SG_SUGGESTED_BY_EMAIL':
    case 'SG_SUGGESTED_BY_PHONE':
    case 'SG_SUGGESTED_BY_ADDRESS':
    case 'SG_SUGGESTED_BY_CLIENT_CODE': {
      // List every client in the proposed cluster. Prefer the rich
      // `members` array (code + name + entityType from dgGroupsDQ);
      // fall back to the raw affectedCodes when member detail wasn't
      // attached (off-roster codes, older rows).
      const proposed = members.length > 0
        ? members
        : (issue.affectedCodes || []).map(code => ({ code }));
      return (
        <div className="cg-key-facts-block">
          <FactLabel>Proposed group:</FactLabel>
          {proposed.length > 0 ? (
            proposed.map(m => (
              <FactValue key={m.code}>
                <ClientChip code={m.code} name={m.name} />
                {m.entityType ? <span className="cg-cell-hint"> ({m.entityType})</span> : null}
              </FactValue>
            ))
          ) : (
            <FactValue><span className="cg-cell-hint">—</span></FactValue>
          )}
        </div>
      );
    }
    default:
      return <span className="cg-cell-hint">—</span>;
  }
}
